import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { useDesignV2 } from '../utils/designV2';
import ReactDOM from 'react-dom';
import ReactJkMusicPlayer from 'react-jinke-music-player';
import 'react-jinke-music-player/assets/index.css';
import Hls from 'hls.js';
import { useSelector, useDispatch } from 'react-redux';
import WaveformOverlay from './WaveformOverlay';
import AudioErrorBoundary from './AudioErrorBoundary';
import './MusicPlayer.scss';
import { useToast } from '../contexts/ToastContext';
import { notifyWebOnly, isWeb, isNative } from '../utils/webOnly';
import { findJinkeQueueItem } from '../utils/jinkeQueueDom';
import { getNativePlaybackEngine } from '../playback/NativePlaybackEngine';
import { track as trackEvent } from '../utils/analytics';

import {
    setAudioList,
    addTrack as addTrackToQueue,
    syncQueueFromPlayer,
    upgradeTrackLink,
    setAutoPlay,
    setPlayMode,
    setMode,
    loadPlaylist,
    toggleAutoRadio,
    addPlayedTrackKey,
    setLikesPlaybackOffset,
    setLikesPlayback,
    setCurrentTrack,
    setIsPlaying,
    setExpanded,
    setNativePlayerState,
} from '../redux/slices/musicPlayerSlice';
import { setListContextIndex, clearListContext } from '../redux/slices/listContextSlice';
import { resolveTrackSrc, resolveMixSrc, sampleUrlOf } from '../utils/resolvePlaybackSrc';
import { prefetchTrackAudio } from '../utils/prefetchNextTrack';
import { mark as markPlayTiming } from '../playback/playTiming';
import { resolveCover, upscaleArtwork } from '../utils/cover';
import { entityId } from '../utils/entityId';
import { buildNativeQueue, queueSignature, resolveQueueStartIndex, spliceNextUpExtras } from '../utils/nativeQueue';
import { refreshAndRetry } from '../redux/utils/refreshAndRetry';
import { isEmptySrcMediaError } from '../utils/isEmptySrcMediaError';
import { setShowSavePlayerModal, triggerPlaylistRefresh } from '../redux/slices/playlistSlice';
import LikeButton from './LikeButton';
import MixLikeButton from './MixLikeButton';
import { setActiveTab } from '../redux/slices/appSlice';
import { setActiveSearch, setTrackQuery, setMixQuery, setArtistQuery } from '../redux/slices/djpalSlice';
import { searchTrack, searchMixes, searchArtist } from '../redux/thunk/djpalThunk';
import CustomPlaylistPanel from './CustomPlaylistPanel';
import { OPEN_QUEUE_EVENT } from '../utils/queueAdd';
import { useTrackBpm } from '../utils/trackBpm';
import MultiArtistText from './MultiArtistText';
import { errMsg } from '../redux/utils/errMsg';
import { startListenRecorder, setNativeClock } from '../utils/listenProgress';

const extractYoutubeId = (url) => {
    if (!url) return null;
    const m = url.match(/[?&]v=([^&]+)/) || url.match(/youtu\.be\/([^?&]+)/);
    return m ? m[1] : null;
};

const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Distinct tracks allowed to fail back-to-back before playback stops.
const MAX_FAILED_IN_A_ROW = 3;

const MusicPlayer = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    const { audioList, autoPlay, playMode, mode, clearPriorAudioLists, autoRadio, playedTrackKeys, likesPlayback, likesPlaybackOffset, currentPlayId } = useSelector((state) => state.musicPlayer);
    const reduxIsPlaying = useSelector((state) => state.musicPlayer.isPlaying);
    // Native: the plugin's playerState mirror — the ONLY source that knows
    // about refill-appended tracks, which never enter jinke's audioList.
    const nativeCurrentTrack = useSelector((state) => (isNative ? state.musicPlayer.currentTrack : null));
    const nativeCurrentTrackRef = useRef(null);
    nativeCurrentTrackRef.current = nativeCurrentTrack;
    // Native plugin clock (AVPlayer / ExoPlayer), written at 4Hz by
    // setNativePlayerState. Stays 0 on web/Electron.
    const nativePosition = useSelector((state) => state.musicPlayer.position);
    const nativeDuration = useSelector((state) => state.musicPlayer.duration);
    const listContext = useSelector((state) => state.listContext);
    const { selectedPlaylistId, selectedPlaylistName } = useSelector((state) => state.playlist);
    const audioInstanceRef = React.useRef(null);
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const [showWaveform, setShowWaveform] = useState(!isMobile);
    const [audioElement, setAudioElement] = useState(null);
    const [portalReady, setPortalReady] = useState(false);
    const [currentTrackInfo, setCurrentTrackInfo] = useState(null);
    // BPM beside the title: the item's own (local tags) or a one-time catalogue lookup.
    const currentBpm = useTrackBpm(currentTrackInfo);
    const [currentTime, setCurrentTime] = useState(0);
    const radioFetchingRef = React.useRef(false);
    // Last radio attempt (seed key + timestamp). jinke re-fires `ended` a few
    // times when the queue sits empty, so a FAILED hand-off attempt would
    // otherwise re-run its whole seed walk on every re-fire. Same-seed
    // attempts inside this window are dropped; a successful attempt advances
    // to a new track (new seed key), so success is never throttled.
    const lastRadioAttemptRef = React.useRef({ key: null, at: 0 });
    const RADIO_ATTEMPT_COOLDOWN_MS = 15000;
    const [customPanelOpen, setCustomPanelOpen] = useState(false);
    // "was added to Next Up" toast link → open (not toggle) the queue panel.
    useEffect(() => {
        const onOpen = () => setCustomPanelOpen(true);
        window.addEventListener(OPEN_QUEUE_EVENT, onOpen);
        return () => window.removeEventListener(OPEN_QUEUE_EVENT, onOpen);
    }, []);
    const audioListRef = React.useRef(audioList);
    const currentTrackInfoRef = React.useRef(null);
    const autoRadioInitRef = React.useRef(true);
    const [duration, setDuration] = useState(0);
    const clearTrackInfoTimer = React.useRef(null);
    const skipInProgressRef = React.useRef(false);
    window.__playerSkipInProgress = skipInProgressRef;
    const hlsRef = useRef(null);
    const volumeWriteTimer = useRef(null);
    const listContextRef = useRef(listContext);
    const listContextAdvancingRef = useRef(false);
    // Tracks sample → stream URL upgrades performed via the background full-play
    // extraction. Keyed by sample URL (jinke's audioList is NOT mutated, so the
    // library's internal state stays stable and doesn't reinit on upgrade).
    const upgradedSrcRef = useRef(new Map());
    // Last known good playback position (seconds), kept current via the
    // 'timeupdate' listener. Used to resume from where we stopped after a
    // mid-play stream re-resolve (expired YouTube/CDN signed URL → 403).
    const lastPositionRef = useRef(0);
    // Guards the re-resolve-on-error path so a genuinely dead link can't
    // loop forever. Keyed by sample URL; cleared shortly after a successful
    // recovery so a later expiry of the same track can recover again.
    const streamRetryRef = useRef(new Set());
    // musicSrcs whose onAudioError recovery is currently awaiting a re-resolve /
    // download. jinke reports ONE failure twice (the element's 'error' event
    // AND its window 'unhandledrejection' hook when play() rejects); the
    // duplicate used to see `alreadyRetried` and skip the track while the
    // first handler was still recovering it.
    const recoveryInFlightRef = useRef(new Set());
    // Distinct musicSrcs that failed since audio last actually played. jinke
    // auto-advances on every load error and our handler skips on top of it,
    // so a queue of dead entries (files on another computer, dead links,
    // offline) walked ~150 tracks in a row, each one starting a priority
    // download, with the play button stuck on a spinner the whole time
    // (2026-10-27). A Set, not a counter: jinke reports one failure twice.
    const failedSinceAudioRef = useRef(new Set());

    // Capacitor hybrid: tracks the URL currently loaded into AVPlayer so
    // we know whether onAudioPlay should issue a `playIndex(idx)` (new
    // track) or `play()` (resume same track). Stays null on web/Electron
    // where the native engine isn't available.
    const nativeAudioUrlRef = useRef(null);
    // Whether the PLUGIN is playing, mirrored from playerState. This is the
    // discriminator the phantom-event guards need. Touch timing cannot work:
    // backgrounding and foregrounding the app are themselves gestures, so
    // "was there a recent touch?" answers yes for exactly the events it is
    // meant to reject.
    const nativeIsPlayingRef = useRef(false);
    // Last url we rebuilt currentTrackInfo for, so the 4Hz playerState stream
    // only triggers that work on an actual track change.
    const lastInfoUrlRef = useRef(null);
    // Last {url, playing} the playerState handler broadcast to row UIs, so
    // the 4Hz stream only dispatches `player-track-change` on an actual
    // track change or play/pause flip — not four times a second.
    const lastNativeRowBroadcastRef = useRef({ url: null, playing: null });
    // Mirror of Redux autoPlay for the native setQueue effect (which only
    // re-runs on audioList changes and must read the CURRENT play intent).
    const autoPlayRef = useRef(false);
    // Set true momentarily when we programmatically write currentTime on
    // jinke's audio element (to reflect native's authoritative position).
    // The 'seeked' listener checks this and skips forwarding the seek
    // back to native — without it, every position-sync write triggered
    // a native seek round-trip, audible as a vinyl-style "jump" each
    // time the app foregrounded after lock.
    const suppressNativeSeekRef = useRef(false);

    // Tracks whether we've already wired the lock-screen remote-command
    // bridge. The native plugin registers AFTER React's mount-time
    // useEffect runs, so the listener can't be attached there — we attach
    // it on first onAudioPlay (which fires after the plugin is up).
    const hybridWiredRef = useRef(false);

    // Tracks whether the user has explicitly paused (in-app pause click
    // OR lock-screen pause). iOS sometimes fires HTML5 audio play/pause
    // events spontaneously when the WebView visibility changes. With
    // this flag + a lastUserTouchRef timestamp, we can distinguish
    // genuine user actions from iOS auto-events and avoid propagating
    // the spurious ones to AVPlayer.
    const userPausedRef = useRef(false);
    const lastUserTouchRef = useRef(0);

    // Playback-lifecycle analytics session. One session per (track, play):
    // created on the first onAudioPlay for a musicSrc, listen time
    // accumulated across pause/resume, finalized by onAudioEnded
    // (track_play_complete) or by the next track starting over an
    // unfinished session (track_skip). Identity is jinke's musicSrc — the
    // sample URL — which survives the lazy-resolve audio.src swap, so an
    // upgrade mid-play never double-counts as a new play.
    const playSessionRef = useRef(null);

    // Network-refusal hint. When the last extraction failure was a
    // bot-challenge / 429 / persistent 403 (cause ambiguous: VPN/datacenter
    // IP reputation OR volume throttling from heavy skipping), the generic
    // "audio unavailable" toasts hide the fact that the fix is on the
    // user's side. Show ONE hedged hint instead — suggests both remedies,
    // asserts no cause, names no source — then stay quiet while it lasts.
    // Resolves true when the hint covered this failure.
    const networkBlockedToastRef = useRef(0);
    const maybeShowNetworkBlockedToast = async () => {
        try {
            const f = await window.electron?.djpal?.lastExtractFailure?.();
            if (f?.reason !== 'network_blocked' || Date.now() - f.at > 5 * 60 * 1000) return false;
            const now = Date.now();
            if (now < networkBlockedToastRef.current) return true; // already hinted
            networkBlockedToastRef.current = now + 3 * 60 * 1000;
            toast.warning('Streaming isn\'t working from your current network. If you\'re using a VPN, try a different server — otherwise wait a few minutes and try again.');
            trackEvent('playback_network_blocked', {});
            return true;
        } catch (_) {
            return false;
        }
    };

    // Sample-fallback toast rate limiter. One failed stream → per-track
    // message. Several within the window (extraction broken app-wide:
    // YouTube 403 wave, audio server down) → ONE collapsed message, then
    // silence while the outage lasts — instead of a toast per track.
    const sampleFallbackToastsRef = useRef({ times: [], suppressedUntil: 0 });
    const showSampleFallbackToast = (trackName) => {
        maybeShowNetworkBlockedToast().then((blocked) => {
            if (blocked) return; // the hint replaces the generic message
            const WINDOW_MS = 120000; // 2 min
            const COLLAPSE_AT = 3;
            const s = sampleFallbackToastsRef.current;
            const now = Date.now();
            s.times = s.times.filter(t => now - t < WINDOW_MS);
            s.times.push(now);
            if (now < s.suppressedUntil) {
                // Outage ongoing — keep the suppression window rolling.
                s.suppressedUntil = now + WINDOW_MS;
                return;
            }
            if (s.times.length >= COLLAPSE_AT) {
                s.suppressedUntil = now + WINDOW_MS;
                toast.info('Full tracks are unavailable right now — playing 30-second previews instead.');
            } else {
                toast.info(`Full track unavailable right now — playing a 30-second preview of ${trackName}.`);
            }
        });
    };

    // Generic "couldn't play" toasts, upgraded to the network hint when a
    // recent extraction failure carried the refusal signature.
    const warnAudioUnavailable = (trackName) => {
        maybeShowNetworkBlockedToast().then((blocked) => {
            if (!blocked) toast.warning(`Skipping ${trackName} — audio unavailable.`);
        });
    };
    const errorAudioUnavailable = (trackName) => {
        // Offline is the single most common cause and the only one the user
        // can act on — say so instead of blaming the source.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            toast.error(`Can’t play ${trackName} — you’re offline. Reconnect and try again.`);
            return;
        }
        maybeShowNetworkBlockedToast().then((blocked) => {
            if (!blocked) toast.error(`Unable to play ${trackName}. The audio source is unavailable.`);
        });
    };

    // Give up on the current track and put the player back in a clickable
    // state.
    //
    // jinke sets its internal `loading` flag when a track starts loading and
    // clears it ONLY on play/canplay — its own onAudioError never does. So any
    // load that ends in failure (offline, dead CDN URL, unsupported codec)
    // leaves the play button spinning forever, and `audioListsPlay` then
    // early-returns on `loading && playId === currentPlayId`, so clicking the
    // same track again does nothing either. Emptying audioLists is the only
    // route from out here into jinke's changeAudioLists → resetAudioStatus →
    // resetAudioPlayStatus, which is what actually clears the flag.
    //
    // Native is exempt: there the audible player is MixGoatAudioPlugin and
    // jinke's muted element is a UI mirror, so its errors say nothing about
    // whether playback is fine — clearing audioList would stop audio the user
    // can hear.
    const resetStuckPlayer = useCallback((reason) => {
        if (isNative && getNativePlaybackEngine().isAvailable()) {
            console.warn('[MusicPlayer] stuck-player reset skipped on native —', reason);
            return;
        }
        console.warn('[MusicPlayer] resetting stuck player —', reason);
        dispatch(setAutoPlay(false));
        dispatch(setAudioList([]));
        // Row-level play/loading UI lives in the feed cards, not in jinke.
        window.dispatchEvent(new CustomEvent('djpal-track-stop-all'));
        window.dispatchEvent(new CustomEvent('djpal-mix-stop'));
        window.dispatchEvent(new CustomEvent('djpal-track-extract-end'));
    }, [dispatch]);

    // Mount-time: track every touch / click so we know when the user
    // genuinely interacted. iOS auto-pause-on-background and auto-resume-
    // on-foreground happen WITHOUT a user touch — that's the distinguishing
    // signal between "user tapped" and "iOS did this on its own".
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const onTouch = () => { lastUserTouchRef.current = Date.now(); };
        window.addEventListener('touchstart', onTouch, { passive: true, capture: true });
        window.addEventListener('mousedown', onTouch, { passive: true, capture: true });
        return () => {
            window.removeEventListener('touchstart', onTouch, { capture: true });
            window.removeEventListener('mousedown', onTouch, { capture: true });
        };
    }, []);

    // 1500ms window: anything within 1.5s of a user touch counts as
    // user-initiated. iOS visibility-change-driven audio events fire
    // immediately on lock/unlock with no preceding touch.
    const isUserInitiatedAction = () => Date.now() - lastUserTouchRef.current < 1500;

    // Pre-warm the native audio session at mount so the first user-
    // initiated play() doesn't pay the synchronous setCategory cost
    // (which was causing the "freeze for a few seconds when starting
    // first track" UX hit). On non-native this is a no-op (engine
    // unavailable). Fire-and-forget; safe to call repeatedly.
    useEffect(() => {
        if (!isNative) return;
        const engine = getNativePlaybackEngine();
        engine.prepare();
    }, []);

    // Pre-warm WKWebView's HTML5 audio infrastructure during splash time.
    //
    // On first-ever install, iOS allocates audio codec buffers + decoder
    // state the FIRST time an <audio> element actually plays a URL. That
    // allocation is multi-hundred-ms and runs on the main thread, which
    // blocks the JS event loop — visible to the user as "tap play,
    // audio starts, but the pause button is unresponsive for 3-5
    // seconds." Subsequent plays reuse the warm infrastructure (which
    // is why the freeze only happens once per install).
    //
    // The standard mobile-audio fix: trigger that cold-init NOW, during
    // splash, with a tiny silent WAV. WKWebView allocates buffers for
    // the warmup, those buffers stay allocated for the session, and the
    // real first play is just an update to warm infrastructure — instant.
    //
    // Muted = true is required for WKWebView autoplay policy to allow
    // this without a user gesture. The audio is silent anyway.
    useEffect(() => {
        if (!isNative) return undefined;

        // Build a tiny 100ms silent 8-bit mono WAV (8000 Hz). ~844 bytes.
        // Embedded inline so there's no network fetch.
        const buildSilentWavDataUri = () => {
            const sampleRate = 8000;
            const numSamples = Math.floor(sampleRate * 0.1); // 100ms
            const dataSize = numSamples;
            const fileSize = 44 + dataSize;
            const buf = new ArrayBuffer(fileSize);
            const view = new DataView(buf);
            view.setUint32(0, 0x52494646, false);  // "RIFF"
            view.setUint32(4, fileSize - 8, true);
            view.setUint32(8, 0x57415645, false);  // "WAVE"
            view.setUint32(12, 0x666d7420, false); // "fmt "
            view.setUint32(16, 16, true);
            view.setUint16(20, 1, true);           // PCM
            view.setUint16(22, 1, true);           // mono
            view.setUint32(24, sampleRate, true);
            view.setUint32(28, sampleRate, true);  // byte rate
            view.setUint16(32, 1, true);           // block align
            view.setUint16(34, 8, true);           // bits per sample
            view.setUint32(36, 0x64617461, false); // "data"
            view.setUint32(40, dataSize, true);
            // 8-bit unsigned PCM silence = 128 (mid-range)
            new Uint8Array(buf, 44).fill(128);
            // base64 encode
            const bytes = new Uint8Array(buf);
            let bin = '';
            for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
            return 'data:audio/wav;base64,' + btoa(bin);
        };

        const warmup = document.createElement('audio');
        warmup.src = buildSilentWavDataUri();
        warmup.muted = true;
        warmup.volume = 0;
        warmup.preload = 'auto';
        // Fire-and-forget. iOS will allocate audio buffers during this
        // play(); if the promise rejects (autoplay block, etc.) we just
        // skip — the worst case is the user pays the cold-init on first
        // real play instead, same as before this fix.
        warmup.play().then(() => warmup.pause()).catch(() => {});

        return () => {
            try {
                warmup.pause();
                // Detach the source without assigning '' — setting src='' fires
                // a spurious MEDIA_ELEMENT_ERROR ("Empty src attribute"). Remove
                // the attribute and load() to release buffers cleanly.
                warmup.removeAttribute('src');
                warmup.load();
            } catch (_) {}
        };
    }, []);

    const [initialVolume] = useState(() => {
        // Prefer the durable store (main-process JSON file) over localStorage —
        // Chromium localStorage isn't reliably flushed on shutdown, so its
        // value can be stale by one session. The sync IPC read is microseconds.
        // Fall back to localStorage when the IPC isn't available (web build).
        const parse = (raw) => {
            if (typeof raw !== 'string') return null;
            const v = parseFloat(raw);
            return Number.isFinite(v) && v >= 0 && v <= 1 ? v : null;
        };
        try {
            const synced = window.electron?.settings?.getSync?.();
            const fromFile = parse(synced?.['musicPlayer:volume']);
            if (fromFile != null) return fromFile;
        } catch {}
        try {
            const fromLs = parse(localStorage.getItem('musicPlayer:volume'));
            if (fromLs != null) return fromLs;
        } catch {}
        return 1;
    });

    // Attach hls.js when playing HLS streams (SoundCloud).
    // The track is dispatched with musicSrc = silent.wav placeholder and the
    // real m3u8 URL in hlsSrc. The audio element plays silence until we call
    // hls.attachMedia, which replaces audio.src with a MediaSource blob and
    // starts real HLS playback. No demux race because audio never sees m3u8.
    //
    // CRITICAL: jinke binds `waiting → loadAndPlayAudio` on the audio element,
    // and loadAndPlayAudio calls `audio.load()` when the buffer is briefly
    // empty — which is exactly what happens during MediaSource attachment.
    // Without suppression, jinke tears down the MediaSource before
    // MEDIA_ATTACHED fires; the silent placeholder then plays to completion
    // and "ends" advances to the next item. We install a capture-phase
    // listener that calls stopImmediatePropagation while hls.js is attached.
    const lastHlsSrc = useRef(null);
    const waitingSuppressInstalledRef = useRef(false);
    useEffect(() => {
        if (!audioList.length) return;
        const hlsTrack = audioList[audioList.length - 1];

        // If the next track is non-HLS but a previous hls.js is still attached,
        // detach so the audio element can play the progressive stream. Without
        // this, hls.js's MediaSource fights the new src and fires bufferAppendError.
        if (!hlsTrack?.isHls && hlsRef.current) {
            try { hlsRef.current.detachMedia(); } catch (_) {}
            try { hlsRef.current.destroy(); } catch (_) {}
            hlsRef.current = null;
            lastHlsSrc.current = null;
        }

        // Real m3u8 URL to attach. Fallback to musicSrc for older queue items
        // that pre-date the hlsSrc field (defensive — no current dispatcher
        // sets isHls without hlsSrc).
        const hlsUrl = hlsTrack?.hlsSrc || (hlsTrack?.isHls ? hlsTrack.musicSrc : null);
        if (!hlsUrl) return;
        // Re-attach if the URL changed OR if we lost the hls instance
        // (a prior attach may have failed or been torn down — don't get stuck
        // on the silent placeholder forever).
        if (hlsUrl === lastHlsSrc.current && hlsRef.current) return;

        let cancelled = false;
        let rafId = null;
        const attach = () => {
            if (cancelled) return;
            const audio = audioInstanceRef.current;
            if (!audio) {
                rafId = requestAnimationFrame(attach);
                return;
            }
            if (!Hls.isSupported()) {
                console.warn('[HLS] Hls.isSupported() returned false — cannot play this mix');
                return;
            }

            if (hlsRef.current) {
                try { hlsRef.current.destroy(); } catch (_) {}
                hlsRef.current = null;
            }

            lastHlsSrc.current = hlsUrl;

            // Block jinke's `waiting` handler from calling audio.load() during
            // MediaSource buffering. Installed once per audio element; the
            // listener no-ops itself when hlsRef.current is null.
            if (!waitingSuppressInstalledRef.current) {
                audio.addEventListener('waiting', (e) => {
                    if (hlsRef.current) {
                        e.stopImmediatePropagation();
                    }
                }, true);
                waitingSuppressInstalledRef.current = true;
            }

            const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
            hlsRef.current = hls;
            hls.loadSource(hlsUrl);
            hls.attachMedia(audio); // overrides audio.src with MediaSource blob
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                audio.play().catch((e) => console.warn('[HLS] play() rejected:', e?.message));
                setCurrentTrackInfo(hlsTrack);
                dispatch(setAutoPlay(true));
                window.__currentPlayerMusicSrc = hlsTrack.musicSrc || null;
                window.__currentPlayerOriginalUrl = hlsTrack.originalUrl || null;
                window.__currentPlayerTrackId = (hlsTrack.trackId ?? hlsTrack.id) != null ? String(hlsTrack.trackId ?? hlsTrack.id) : null;
                window.__currentPlayerMixId = hlsTrack.mixId != null ? String(hlsTrack.mixId) : null;
                window.dispatchEvent(new CustomEvent('player-track-change', {
                    detail: { musicSrc: hlsTrack.musicSrc, originalUrl: hlsTrack.originalUrl || null, trackId: window.__currentPlayerTrackId, mixId: window.__currentPlayerMixId, playing: true },
                }));
            });
            hls.on(Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    console.error('[HLS] Fatal error:', data.type, data.details);
                    try { hls.destroy(); } catch (_) {}
                    hlsRef.current = null;
                    lastHlsSrc.current = null;
                } else {
                    console.warn('[HLS] Non-fatal:', data.type, data.details);
                }
            });
        };
        attach();

        return () => {
            cancelled = true;
            if (rafId) cancelAnimationFrame(rafId);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioList]);

    // Spacebar play/pause — skip when user is typing in an input
    const handleKeyDown = useCallback((e) => {
        if (e.code !== 'Space') return;
        const tag = e.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
        e.preventDefault();
        const audio = window.musicPlayerInstance;
        if (!audio) return;
        if (audio.paused) {
            audio.play?.()?.catch(() => {});
        } else {
            audio.pause?.();
        }
    }, []);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    // Open an artist page in Mixpedia. Prefers the resolved artist id when
    // available; otherwise searches by name. Sub-tab + query BEFORE
    // setActiveTab('djpal') — the nav-history marker pushed on the tab switch
    // must be a clean 'artist' marker the search result REPLACES, so one Back
    // returns here instead of stopping at Mixpedia home (same trap as
    // RowActions.searchInMixpedia).
    const handleOpenArtist = useCallback(({ id, name, artworkUrl }) => {
        if (!name) return;
        dispatch(setActiveSearch('artist'));
        dispatch(setArtistQuery(name));
        dispatch(setActiveTab('djpal'));
        dispatch(searchArtist({ artistName: name, artistId: id || null, artworkUrl: artworkUrl || null }));
    }, [dispatch]);

    // Open the currently-playing track or mix in Mixpedia.
    // Mix lookup priority:
    //   1. public_id  — exact match if we have it
    //   2. link        — the mix's source URL uniquely identifies it (avoids
    //                    showing every "Residency" mix when several DJs have one)
    //   3. artist + title — narrows by DJ when no link is available
    //   4. title       — last resort
    // Tracks fall back to "Artist - Title" since the track-search API is name-based.
    // Sub-tab + query BEFORE setActiveTab('djpal'): the nav-history marker pushed
    // on the tab switch must already be typed so the search result REPLACES it —
    // one Back returns to the originating tab (e.g. For You), not Mixpedia home.
    const handleSearchInMixpedia = useCallback(() => {
        const info = currentTrackInfoRef.current;
        if (!info?.singer || !info?.name) return;
        if (info.isMix) {
            const rawTitle = info.mixTitle || info.name;
            const mixQuery = info.singer ? `${info.singer} - ${rawTitle}` : rawTitle;
            // Id-first, no text-search fallback: queue entries carry the real
            // catalog id in mixId (mix.id is never fabricated anymore), and a
            // playing mix always has originalUrl — an exact unique key — for
            // the rare non-catalog case. Clicks navigate by id; only the
            // search box searches by text.
            const filters = info.mixId ? { public_id: info.mixId }
                : info.originalUrl ? { link: info.originalUrl }
                : null;
            if (!filters) return;
            dispatch(setActiveSearch('mix'));
            dispatch(setMixQuery(mixQuery));
            dispatch(setActiveTab('djpal'));
            dispatch(searchMixes({ filters }));
        } else {
            const query = `${info.singer} - ${info.name}`;
            dispatch(setActiveSearch('track'));
            dispatch(setTrackQuery(query));
            dispatch(setActiveTab('djpal'));
            // trackId = catalog id/public_id for Mixpedia-originated tracks
            // (entityId at enqueue time); null for genuinely-local files.
            dispatch(searchTrack({ trackName: query, trackId: info.trackId || null, variant: 'A', artworkUrl: info.cover || null }));
        }
    }, [dispatch]);

    // Global error handler for audio/video element errors
    useEffect(() => {
        const handleGlobalMediaError = (event) => {
            const target = event.target;
            // Check if error is from audio/video element
            if (target && (target.tagName === 'AUDIO' || target.tagName === 'VIDEO')) {
                // preventDefault() only — NOT stopPropagation(). This runs in
                // the CAPTURE phase on window; stopping propagation here kills
                // the event before it reaches the <audio> element, where
                // jinke binds its own `error` → onAudioError listener. That
                // callback owns recovery (row-UI reset, list advance, sample
                // fallback, toast). The element-level error listener below has
                // the same warning for the same reason — keep them in sync.
                event.preventDefault();
                // Benign "no track loaded yet" empty-src state — stay silent.
                if (!isEmptySrcMediaError(target)) {
                    console.warn('[MusicPlayer] Media error caught globally:', target.error);
                }
                // Don't show toast here - onAudioError callback will handle it
                return false;
            }
        };

        // Suppress console errors from audio loading failures
        const originalConsoleError = console.error;
        console.error = (...args) => {
            const errorString = args.join(' ');
            // Suppress audio-related errors that are already handled
            if (errorString.includes('no supported source') ||
                errorString.includes('Failed to load') ||
                errorString.includes('MEDIA_ERR_') ||
                errorString.includes('NotSupportedError')) {
                console.warn('[MusicPlayer] Suppressed audio error:', ...args);
                return;
            }
            originalConsoleError.apply(console, args);
        };

        // Capture phase to catch errors before they bubble
        window.addEventListener('error', handleGlobalMediaError, true);

        return () => {
            window.removeEventListener('error', handleGlobalMediaError, true);
            console.error = originalConsoleError;
        };
    }, []);

    // Expose audio instance globally for external control
    useEffect(() => {
        if (audioInstanceRef.current) {
            window.musicPlayerInstance = audioInstanceRef.current;
            setAudioElement(audioInstanceRef.current);

            // Log audio errors for debugging — but DO NOT call stopPropagation:
            // we listen in capture phase, and stopPropagation here blocks the
            // library's own error handler from firing, which breaks our
            // onAudioError → toast / sample-fallback / cache-invalidate chain.
            // The console noise from these errors is already suppressed via
            // the console.error monkey-patch above.
            const handleAudioError = (e) => {
                console.warn('[MusicPlayer] Audio element error:', e?.target?.error?.code, e?.target?.error?.message || '');
            };

            // The library only binds its own volumechange listener inside
            // initPlayer, which early-returns when audioLists is empty —
            // so volume changes made before any track has played never
            // reach the `onAudioVolumeChange` prop callback. Listen on the
            // element ourselves so the slider persists regardless of
            // whether a track is loaded. audio.volume is `bar²` (the
            // library applies a perceptual curve); save the bar value
            // (sqrt) so `defaultVolume` on the next mount round-trips.
            const handleVolumeChange = () => {
                const el = audioInstanceRef.current;
                if (!el) return;
                const barValue = Math.sqrt(el.volume);
                // localStorage for fast first-paint on next launch.
                try { localStorage.setItem('musicPlayer:volume', String(barValue)); } catch {}
                // Durable disk write — Chromium's localStorage flush isn't
                // reliable enough for "last write before quit"; values from
                // a slider drag often don't survive an app restart. The
                // main-process settings file (mixgoat-app-settings.json) is
                // written synchronously via fs.writeFileSync.
                try { window.electron?.settings?.set?.('musicPlayer:volume', String(barValue)); } catch {}
            };

            audioInstanceRef.current.addEventListener('error', handleAudioError, true);
            audioInstanceRef.current.addEventListener('volumechange', handleVolumeChange);

            // ── Capacitor hybrid: mute HTML5 audio, route playback to native ──
            // jinke's HTML5 <audio> drives the UI (time, duration, waveform,
            // queue) but the actual sound + lock-screen widget come from
            // MixGoatAudioPlugin (AVPlayer). Both load the same URL and run
            // at 1.0x rate, so they stay in close sync for short samples.
            // Only enabled if the plugin actually registered — otherwise
            // we leave HTML5 audio alone so playback still works.
            let handleNativeSeek = null;
            let offRemoteCommand = null;
            const nativeEngine = isNative ? getNativePlaybackEngine() : null;
            if (nativeEngine && nativeEngine.isAvailable()) {
                // Mute HTML5 so it doesn't double-play. iOS volume buttons
                // control AVPlayer directly through the audio session.
                audioInstanceRef.current.volume = 0;
                audioInstanceRef.current.muted = true;
                // Forward scrubber seeks to AVPlayer — but ONLY if the
                // seek came from the user (drag the progress bar). Our
                // own programmatic writes (sync against native's clock
                // on resume) set suppressNativeSeekRef to true for the
                // duration of the write, so the seeked event triggered
                // by those writes is skipped — otherwise every sync
                // write would round-trip back to native as a seek,
                // causing an audible "jump" on every reconcile.
                handleNativeSeek = () => {
                    // NO-OP on native. jinke's <audio> fires 'seeked' for
                    // reasons we can't trust — its own loop reset, src
                    // change, end-of-sample restart, etc. Propagating
                    // those to engine.seek caused the native track to
                    // restart from 0 every time jinke's muted audio
                    // hit its sample's end. Real user scrubs from
                    // NowPlayingPage now call engine.seek directly.
                    return;
                };
                audioInstanceRef.current.addEventListener('seeked', handleNativeSeek);
                // Lock-screen / control-center buttons → drive HTML5 audio
                // so jinke's UI stays in sync (mini-player play icon, etc.).
                // The AVPlayer plugin already actuates AVPlayer itself before
                // emitting the event, so we just mirror the state into HTML5.
                offRemoteCommand = nativeEngine.on('remoteCommand', ({ action }) => {
                    const el = audioInstanceRef.current;
                    if (!el) return;
                    if (action === 'play') {
                        el.play().catch(() => {});
                        // Mirror to Redux so the in-app UI matches the
                        // lock-screen state. Without this, autoPlay can
                        // stay false and the unlock flow re-pauses things.
                        dispatch(setAutoPlay(true));
                        dispatch(setIsPlaying(true));
                    } else if (action === 'pause') {
                        el.pause();
                        // CRITICAL: clear autoPlay too, otherwise jinke's
                        // state machine re-issues a play() on the next
                        // tick (e.g., when the WebView resumes from lock
                        // and React re-renders), which is exactly the
                        // "tap pause on lock screen → unlock → music
                        // starts again" symptom the user reported.
                        dispatch(setAutoPlay(false));
                        dispatch(setIsPlaying(false));
                    }
                    // 'next' / 'prev' are no-ops for now — single-track samples
                    // dominate the queue; we can wire to jinke's onAudioPlay
                    // hooks for next-track handling later if needed.
                });
            }

            return () => {
                if (audioInstanceRef.current) {
                    audioInstanceRef.current.removeEventListener('error', handleAudioError, true);
                    audioInstanceRef.current.removeEventListener('volumechange', handleVolumeChange);
                    if (handleNativeSeek) {
                        audioInstanceRef.current.removeEventListener('seeked', handleNativeSeek);
                    }
                }
                if (offRemoteCommand) offRemoteCommand();
            };
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioInstanceRef.current]);

    // Track current time and duration for waveform overlay.
    //
    // NATIVE USES A DIFFERENT CLOCK — see the effect below. jinke's muted
    // element is not playing what you hear: it stays on the originally
    // tapped sample and its currentTime freezes once WKWebView suspends
    // it, so listening to it here produced a progress ring that showed
    // nothing on an auto-advanced track and then advanced in ~1.5s snaps
    // (the position-sync threshold) instead of flowing.
    useEffect(() => {
        if (!audioElement || isNative) return;
        const onTimeUpdate = () => {
            const t = audioElement.currentTime || 0;
            setCurrentTime(t);
            // Remember the last position > 0 so a stream re-resolve after a
            // 403/expiry can resume from here instead of restarting.
            if (t > 0) lastPositionRef.current = t;
        };
        const onDurationChange = () => setDuration(audioElement.duration || 0);
        // Native: zero the mini-player progress ring the moment a new src
        // starts loading. Without this the ring keeps the PREVIOUS track's
        // percentage until the new sample's first timeupdate arrives —
        // visible as a stale ring on every row tap.
        const onLoadStart = () => {
            if (!isNative) return;
            setCurrentTime(0);
            setDuration(0);
        };
        audioElement.addEventListener('timeupdate', onTimeUpdate);
        audioElement.addEventListener('durationchange', onDurationChange);
        audioElement.addEventListener('loadstart', onLoadStart);
        return () => {
            audioElement.removeEventListener('timeupdate', onTimeUpdate);
            audioElement.removeEventListener('durationchange', onDurationChange);
            audioElement.removeEventListener('loadstart', onLoadStart);
        };
    }, [audioElement]);

    // Native: the plugin IS the clock. It publishes position + duration at
    // 4Hz (plus every transition) into Redux via setNativePlayerState, and
    // that is the only source that describes the audio actually coming out
    // of the speaker — including tracks it auto-advanced to on its own.
    // Feeding the same `currentTime` / `duration` state the web path uses
    // keeps every consumer (mini-player ring, WaveformOverlay, the
    // lastPosition bookkeeping) working unchanged.
    //
    // 250ms granularity is a real step down from a media element's
    // timeupdate cadence, but it moves steadily, which is what "flowing"
    // means here. If it ever needs to be smoother, interpolate between
    // ticks with rAF rather than going back to reading the muted element.
    useEffect(() => {
        if (!isNative) return;
        const t = Number.isFinite(nativePosition) ? nativePosition : 0;
        setCurrentTime(t);
        if (t > 0) lastPositionRef.current = t;
    }, [nativePosition]);

    // Listen memory for the For You hero cards (utils/listenProgress.js): one
    // 1 s recorder for the app's lifetime. On phones the native plugin is the
    // clock, so mirror its position where the recorder can read it.
    useEffect(() => { startListenRecorder(); }, []);
    useEffect(() => {
        if (!isNative) return;
        setNativeClock({
            active: getNativePlaybackEngine().isAvailable(),
            position: Number.isFinite(nativePosition) ? nativePosition : 0,
            duration: Number.isFinite(nativeDuration) ? nativeDuration : 0,
            playing: !!reduxIsPlaying,
            seek: (sec) => getNativePlaybackEngine().seek(sec),
        });
    }, [nativePosition, nativeDuration, reduxIsPlaying]);

    useEffect(() => {
        if (!isNative) return;
        setDuration(Number.isFinite(nativeDuration) && nativeDuration > 0 ? nativeDuration : 0);
    }, [nativeDuration]);

    // ─── Stalled-load watchdog ────────────────────────────────
    // onAudioError is the recovery path for a load that FAILS. A load that
    // simply never finishes has no such path: when connectivity drops
    // mid-fetch the element fires `waiting`/`stalled` and then nothing at all
    // — no `error`, so jinke's `loading` flag stays true and the play button
    // spins forever with no message.
    //
    // Arm on loadstart, disarm the instant real audio comes out. If neither
    // happens within the window, treat it as a failed load: say why, and reset
    // so the player is clickable again.
    //
    // Native is excluded for the same reason as resetStuckPlayer.
    useEffect(() => {
        if (!audioElement || isNative) return undefined;
        const STALL_MS = 30000;
        let timer = null;
        const disarm = () => { if (timer) { clearTimeout(timer); timer = null; } };
        const onStall = () => {
            timer = null;
            const el = audioElement;
            // Buffered or already producing audio — not a stall.
            if (!el || el.readyState >= 3 || el.currentTime > 0) return;
            // Only a load we're WAITING ON counts. `paused` can't tell us that
            // (it stays true while jinke waits for canplay before calling
            // play(), and false the instant play() is invoked), so use the
            // intent flag every play path sets. A track sitting in the queue
            // with autoPlay off is idle, not stuck.
            if (!autoPlayRef.current && el.paused) return;
            // The user pausing mid-load stops the fetch; that's their doing.
            if (userPausedRef.current) return;
            const name = currentTrackInfoRef.current?.name || 'this track';
            console.warn('[MusicPlayer] Load stalled with no error event —', el.currentSrc || el.src);
            const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
            toast.error(offline
                ? `Can’t play ${name} — you’re offline. Reconnect and try again.`
                : `${name} took too long to load. Check your connection and try again.`);
            trackEvent('playback_stalled', { offline });
            resetStuckPlayer('load stalled with no error event');
        };
        const arm = () => { disarm(); timer = setTimeout(onStall, STALL_MS); };
        audioElement.addEventListener('loadstart', arm);
        audioElement.addEventListener('playing', disarm);
        audioElement.addEventListener('canplaythrough', disarm);
        audioElement.addEventListener('error', disarm);   // onAudioError owns it
        audioElement.addEventListener('emptied', disarm);
        audioElement.addEventListener('abort', disarm);
        return () => {
            disarm();
            audioElement.removeEventListener('loadstart', arm);
            audioElement.removeEventListener('playing', disarm);
            audioElement.removeEventListener('canplaythrough', disarm);
            audioElement.removeEventListener('error', disarm);
            audioElement.removeEventListener('emptied', disarm);
            audioElement.removeEventListener('abort', disarm);
        };
    }, [audioElement, toast, resetStuckPlayer]);

    // ─── Mid-track stall recovery ─────────────────────────────
    // The watchdog above only covers a track that never started. Once audio
    // HAS been playing, a reset is the wrong answer — it throws away the
    // user's position on something that usually comes back. But doing nothing
    // is what left the spinner running forever at 1:23 when the wifi dropped:
    // the element goes quiet with no `error`, jinke's `waiting` handler pins
    // `loading` true, and the position freezes.
    //
    // So: recover in place first. Re-request the same URL and seek back to
    // where we were — that fixes the reconnect case with nothing worse than a
    // pause, and needs no re-extraction (a 403/expired stream fires a real
    // `error` and is onAudioError's job, not this one). Only if the recovery
    // ALSO produces nothing do we give up and clear, and by then the track is
    // dead anyway so there's no position left worth keeping.
    //
    // Native excluded for the same reason as resetStuckPlayer.
    useEffect(() => {
        if (!audioElement || isNative) return undefined;
        const STALL_MS = 15000;   // position frozen this long → try to recover
        const RECOVER_MS = 20000; // recovery produced nothing → give up
        const el = audioElement;
        let timer = null;
        let recovering = false;   // our own load() is in flight
        let markPos = 0;          // position when we armed / resumed from
        let markSrc = '';         // and the src it belonged to

        const clear = () => { if (timer) { clearTimeout(timer); timer = null; } };
        const reset = () => { clear(); recovering = false; };
        // A different track is loaded than the one we armed on — anything we
        // were waiting for is void. Without this the giveUp timer from a
        // stalled track could fire on its REPLACEMENT (whose currentTime is
        // legitimately small) and clear a player that is playing fine.
        const superseded = () => (el.currentSrc || el.src || '') !== markSrc;

        const giveUp = () => {
            timer = null;
            if (superseded() || el.currentTime > markPos) return reset();
            const name = currentTrackInfoRef.current?.name || 'this track';
            const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
            console.warn('[MusicPlayer] Mid-track recovery failed, clearing —', el.currentSrc || el.src);
            toast.error(offline
                ? 'Playback stopped — you’re offline. Reconnect and try again.'
                : `Lost the audio stream for ${name}. Check your connection and try again.`);
            trackEvent('playback_stalled', { offline, mid_track: true });
            reset();
            resetStuckPlayer('mid-track stall did not recover');
        };

        const recover = () => {
            timer = null;
            if (superseded()) return reset();
            // Advanced since we armed, or buffered up — it recovered on its own.
            if (el.currentTime > markPos || el.readyState >= 3) return reset();
            // The user pausing stops the fetch; that's their doing, not a stall.
            if (el.paused || userPausedRef.current) return reset();

            const resumeAt = el.currentTime || lastPositionRef.current || 0;
            console.warn('[MusicPlayer] Playback stalled mid-track — reloading and resuming at', resumeAt);
            const restore = () => {
                el.removeEventListener('loadedmetadata', restore);
                try {
                    if (resumeAt > 0 && (!el.duration || resumeAt < el.duration)) el.currentTime = resumeAt;
                } catch (_) { /* seek rejected — restarting beats a dead spinner */ }
                el.play?.()?.catch(() => { /* the giveUp timer is the backstop */ });
            };
            el.addEventListener('loadedmetadata', restore);
            markPos = resumeAt;
            recovering = true;
            timer = setTimeout(giveUp, RECOVER_MS);
            try {
                el.load();
            } catch (_) {
                giveUp();
            }
        };

        // `waiting`/`stalled` = the element wants bytes it doesn't have. Normal
        // during rebuffering — only the timer actually firing does anything.
        const arm = () => {
            if (recovering || timer) return;
            markPos = el.currentTime;
            // Never-started is the other watchdog's job, not ours.
            if (!(markPos > 0)) return;
            markSrc = el.currentSrc || el.src || '';
            timer = setTimeout(recover, STALL_MS);
        };
        // Real forward progress cancels everything, including a pending giveUp.
        const onProgress = () => { if (el.currentTime > markPos) reset(); };
        // `emptied` fires from OUR load() too — ignore it while recovering, or
        // we would cancel the backstop we just armed.
        const onEmptied = () => { if (!recovering) reset(); };

        el.addEventListener('waiting', arm);
        el.addEventListener('stalled', arm);
        el.addEventListener('timeupdate', onProgress);
        el.addEventListener('playing', onProgress);
        el.addEventListener('emptied', onEmptied);
        el.addEventListener('error', reset);   // onAudioError owns real errors
        return () => {
            reset();
            el.removeEventListener('waiting', arm);
            el.removeEventListener('stalled', arm);
            el.removeEventListener('timeupdate', onProgress);
            el.removeEventListener('playing', onProgress);
            el.removeEventListener('emptied', onEmptied);
            el.removeEventListener('error', reset);
        };
    }, [audioElement, toast, resetStuckPlayer]);

    // Drive the mini-player cover progress ring (see MusicPlayer.scss
    // `.img-content::after`). Writing a CSS variable on :root keeps the
    // ring cheap to update — only the conic-gradient repaints, no React
    // re-render of the jinke-owned panel.
    useEffect(() => {
        const pct = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
        document.documentElement.style.setProperty('--mini-progress', pct);
    }, [currentTime, duration]);

    // ─── Lock-screen / Now Playing card (Media Session API) ─────────
    // Without this, iOS shows a generic "Mixgoat" thumbnail with ±10s
    // skip buttons (its default behavior for any web <audio> playing
    // in the background). Setting metadata + handlers makes the lock
    // screen show the real track artwork, title/artist, and prev/next
    // controls — matching what SoundCloud / Spotify display natively.
    //
    // Note: the Web Media Session API does not expose a "like" action
    // that maps to a lock-screen heart on iOS — the favorite/star slot
    // is reserved for AirPlay & Apple Music tracks. So we can't add
    // a like button to the lock card the way the SoundCloud native
    // app does. Best we can do is the artwork + title + transport.
    useEffect(() => {
        if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
        // On Capacitor native, MixGoatAudioPlugin owns MPRemoteCommandCenter
        // + MPNowPlayingInfoCenter. If we ALSO register navigator.mediaSession
        // handlers here, iOS fires BOTH on every lock-screen tap — and the JS
        // handlers re-trigger jinke's audio element (via audio.play()), which
        // then fires onAudioPlay, which calls engine.resume(). User taps
        // pause on lock → unlocks → music resumes "out of nowhere". The
        // native plugin path is sufficient; skip the redundant web layer.
        if (isNative && getNativePlaybackEngine().isAvailable()) {
            try { navigator.mediaSession.metadata = null; } catch {}
            return;
        }
        const info = currentTrackInfo;
        if (!info?.name) {
            try { navigator.mediaSession.metadata = null; } catch {}
            return;
        }

        // Upscale for the lock screen, which renders at ~512px and makes the
        // mini-player thumbnail look pixelated. Shared with NowPlayingPage's
        // hero via utils/cover — this used to be a local copy whose regex was
        // anchored at end-of-string and so silently missed Apple's
        // `100x100bb-60.jpg` compression suffix, leaving the lock screen on
        // the thumbnail. Sharing it also means the hero and the lock screen
        // now ask for the SAME url, so one fetch serves both.
        const artworkUrl = upscaleArtwork(info.cover) || '';
        // No prefetch here: this effect has already returned above on native,
        // and NowPlayingPage warms the same url anyway. Its artwork effect
        // sits ABOVE its `!expanded` early return, so it runs while the page
        // is still collapsed — by the time the user opens it the hero is
        // decoded and paints on the first frame (measured: 26ms, at 600x600).

        try {
            navigator.mediaSession.metadata = new window.MediaMetadata({
                title: info.name || '',
                artist: info.singer || '',
                album: info.isMix ? 'Mix' : 'MixGoat',
                artwork: artworkUrl ? [
                    { src: artworkUrl, sizes: '512x512', type: 'image/jpeg' },
                ] : [],
            });
        } catch (_) {}

        const setHandler = (action, fn) => {
            try { navigator.mediaSession.setActionHandler(action, fn); } catch {}
        };

        setHandler('play', () => {
            const audio = window.musicPlayerInstance;
            if (!audio) return;
            // After a long pause with the screen locked, iOS Safari
            // suspends the audio element — buffered bytes are freed and
            // the source connection dropped. A bare audio.play() then
            // rejects with NotAllowedError/AbortError, and iOS yanks
            // the lock-screen card. Recovery: re-load the source (Apple
            // preview CDN is stable, doesn't expire), wait for canplay
            // (loadedmetadata is too early on iOS — the demuxer hasn't
            // produced playable data yet), restore time, then play.
            const tryRecover = () => {
                const src = audio.currentSrc || audio.src;
                if (!src) return;
                const t = audio.currentTime || 0;
                try { audio.load(); } catch {}
                const onCanPlay = () => {
                    audio.removeEventListener('canplay', onCanPlay);
                    try { audio.currentTime = t; } catch {}
                    audio.play().catch(() => {});
                };
                audio.addEventListener('canplay', onCanPlay);
            };
            const p = audio.play();
            if (p && typeof p.catch === 'function') p.catch(tryRecover);
        });
        setHandler('pause', () => {
            const audio = window.musicPlayerInstance;
            if (audio) audio.pause();
        });
        setHandler('previoustrack', () => {
            const prevBtn = document.querySelector('.react-jinke-music-player-main .player-content .prev-audio');
            if (prevBtn) prevBtn.click();
        });
        setHandler('nexttrack', () => {
            const nextBtn = document.querySelector('.react-jinke-music-player-main .player-content .next-audio');
            if (nextBtn) nextBtn.click();
        });
        // Explicitly null the seek handlers so iOS uses prev/next
        // arrows on the lock screen instead of the ±10s skip controls
        // (which iOS shows by default when seek handlers are unset and
        // the audio reports a finite duration).
        setHandler('seekbackward', null);
        setHandler('seekforward', null);
        // Enable dragging the lock-screen scrubber.
        setHandler('seekto', (details) => {
            const audio = window.musicPlayerInstance;
            if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
            if (typeof details?.seekTime !== 'number') return;
            if (details.fastSeek && typeof audio.fastSeek === 'function') {
                try { audio.fastSeek(details.seekTime); return; } catch {}
            }
            try { audio.currentTime = Math.max(0, Math.min(audio.duration, details.seekTime)); } catch {}
        });

        return () => {
            ['play', 'pause', 'previoustrack', 'nexttrack', 'seekbackward', 'seekforward', 'seekto']
                .forEach((a) => { try { navigator.mediaSession.setActionHandler(a, null); } catch {} });
        };
    }, [currentTrackInfo]);

    // Keep mediaSession.playbackState in sync with the audio element so
    // the lock-screen play/pause icon flips correctly.
    useEffect(() => {
        if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
        if (!audioElement) return;
        const onPlay = () => { try { navigator.mediaSession.playbackState = 'playing'; } catch {} };
        const onPause = () => { try { navigator.mediaSession.playbackState = 'paused'; } catch {} };
        audioElement.addEventListener('play', onPlay);
        audioElement.addEventListener('pause', onPause);
        return () => {
            audioElement.removeEventListener('play', onPlay);
            audioElement.removeEventListener('pause', onPause);
        };
    }, [audioElement]);

    // Periodically update mediaSession.positionState so the lock-screen
    // progress bar tracks the playhead.
    useEffect(() => {
        if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
        if (!audioElement) return;
        if (!('setPositionState' in navigator.mediaSession)) return;
        let raf = 0;
        const tick = () => {
            const dur = audioElement.duration;
            const t = audioElement.currentTime;
            if (Number.isFinite(dur) && dur > 0 && Number.isFinite(t)) {
                try {
                    navigator.mediaSession.setPositionState({
                        duration: dur,
                        position: Math.min(t, dur),
                        playbackRate: audioElement.playbackRate || 1,
                    });
                } catch {}
            }
            raf = window.setTimeout(tick, 1000); // 1Hz is plenty for lock-screen
        };
        tick();
        return () => { window.clearTimeout(raf); };
    }, [audioElement, currentTrackInfo]);

    // ─── iOS background-audio: known limitation ────────────────────
    // iOS Safari aggressively reclaims paused <audio> elements while
    // the screen is locked. After ~30s-2min: buffers freed, source
    // socket closed. After ~5min: the entire MediaSession is discarded
    // and iOS shows "Not Playing" then yanks the lock-screen card.
    //
    // Things we tried that DIDN'T work:
    // - Silent <audio> heartbeat at volume=0 (iOS ignores 0-volume
    //   audio for session liveness; 15+ tightened the rule)
    // - Re-emitting MediaSession metadata on visibilitychange (iOS
    //   only honors metadata changes while the session is alive; once
    //   suspended, no JS-side trick survives)
    // - audio.load() during the lock-screen play handler (works for
    //   the 30s-2min window, fails after iOS evicts the page entirely)
    //
    // What DOES work and is shipped:
    // - audio.load() + canplay retry on play-handler failure (covers
    //   the short-pause case, see the 'play' MediaSession handler above)
    // - visibilitychange recovery: pre-warm the source when the user
    //   comes back to the tab (effect below)
    // - Stalled detection: if the audio reports stalled/error events,
    //   surface a toast pointing the user at the recovery action
    //
    // The bulletproof fix is native AVPlayer (Capacitor path #2). On
    // web, this is a known platform limitation — Apple Music Web has
    // the same constraint. Don't add more workarounds here without a
    // strong reason; future-you will thank past-you.

    // When the tab becomes visible again (user unlocks the phone, swipes
    // back to Safari from another app), iOS may have already suspended
    // the audio source even though we're still showing as the active
    // media session. Pre-emptively re-load the source so the next user
    // tap on play has fresh buffers, AND refresh the MediaSession
    // metadata so iOS doesn't treat the session as stale.
    useEffect(() => {
        if (typeof document === 'undefined') return;
        // iOS-only recovery. On desktop, audio.load() forces hls.js to tear
        // down + reattach the MediaSource (manifest re-fetch, demuxer rebuild)
        // which stalls the main thread for ~seconds on every app refocus.
        if (!isWeb) return;
        const isTouch = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
        if (!isTouch) return;
        const onVisible = () => {
            if (document.visibilityState !== 'visible') return;
            const audio = audioElement;
            if (!audio) return;
            // Re-poke MediaSession so iOS knows this app is still the
            // owner of the lock-screen card.
            if ('mediaSession' in navigator && navigator.mediaSession.metadata) {
                try {
                    // Re-assign the same metadata to refresh the session.
                    // Cloning to a new MediaMetadata triggers iOS's
                    // "session updated" code path.
                    const m = navigator.mediaSession.metadata;
                    navigator.mediaSession.metadata = new window.MediaMetadata({
                        title: m.title, artist: m.artist, album: m.album,
                        artwork: m.artwork ? Array.from(m.artwork) : [],
                    });
                } catch {}
            }
            // If the audio has nothing buffered (iOS dropped it), reload
            // so the next play() doesn't have to fight a cold source.
            try {
                if (audio.paused && audio.networkState === audio.NETWORK_IDLE && audio.readyState < 2) {
                    const t = audio.currentTime || 0;
                    audio.load();
                    const restore = () => {
                        audio.removeEventListener('loadedmetadata', restore);
                        try { audio.currentTime = t; } catch {}
                    };
                    audio.addEventListener('loadedmetadata', restore);
                }
            } catch {}
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [audioElement]);

    // ─── Stalled-resume toast (web mobile only) ─────────────────────
    // When iOS suspends our audio session and the lock-screen "Not
    // Playing" card vanishes, the user comes back to the app having
    // no idea WHY playback stopped. Detect the stall (audio's
    // 'stalled' event fires when iOS drops the source mid-fetch) and
    // show a short, honest toast pointing them at the recovery
    // action. Touch-only — desktop browsers don't suspend like this.
    //
    // Suppressed once per page load (toastedRef) — the same suspension
    // can fire 'stalled' repeatedly while iOS reclaims memory, and we
    // don't want a stack of identical toasts.
    const stalledToastRef = React.useRef(false);

    // Real audio progress closes a failure streak (see failedSinceAudioRef).
    useEffect(() => {
        if (!audioElement) return;
        const onProgress = () => {
            if (audioElement.currentTime > 1 && failedSinceAudioRef.current.size) failedSinceAudioRef.current = new Set();
        };
        audioElement.addEventListener('timeupdate', onProgress);
        return () => audioElement.removeEventListener('timeupdate', onProgress);
    }, [audioElement]);

    // When a playlist holds tracks whose files aren't on this machine (e.g.
    // synced from another computer), jinke walks the queue and each missing
    // file fires onAudioError in quick succession. Aggregate those into ONE
    // toast on a short debounce so the user gets a single, readable message
    // ("N tracks aren't on this device…") instead of a flood that flashes by.
    const missingFileToastRef = React.useRef({ count: 0, timer: null });
    const flushMissingFileToast = React.useCallback(() => {
        const n = missingFileToastRef.current.count;
        missingFileToastRef.current = { count: 0, timer: null };
        if (n <= 0) return;
        // No count in the copy — the number reflects what happened to reach the
        // player, not the playlist, which reads as wrong when the whole
        // playlist is off-device.
        toast.warning(
            n === 1
                ? `A track in this playlist isn't on this device — it was synced from another device.`
                : `Tracks in this playlist aren't on this device — they were synced from another device.`,
            7000
        );
    }, [toast]);
    const reportMissingFile = React.useCallback(() => {
        const ref = missingFileToastRef.current;
        ref.count += 1;
        if (ref.timer) clearTimeout(ref.timer);
        ref.timer = setTimeout(flushMissingFileToast, 500);
    }, [flushMissingFileToast]);

    useEffect(() => {
        if (!audioElement) return;
        if (!isWeb) return;
        const isTouch = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
        if (!isTouch) return;
        const onStalled = () => {
            // Only nudge if we genuinely lost the source, not for
            // benign buffering hiccups during normal playback.
            if (!audioElement.paused) return;
            if (audioElement.readyState >= 2) return;
            if (stalledToastRef.current) return;
            stalledToastRef.current = true;
            toast.info('iOS paused playback in the background. Tap play to resume.');
        };
        // Reset the gate when a fresh play succeeds (so the next
        // suspension event is allowed to surface a toast).
        const onPlay = () => { stalledToastRef.current = false; };
        audioElement.addEventListener('stalled', onStalled);
        audioElement.addEventListener('play', onPlay);
        return () => {
            audioElement.removeEventListener('stalled', onStalled);
            audioElement.removeEventListener('play', onPlay);
        };
    }, [audioElement, toast]);

    // Load saved playlist on mount
    useEffect(() => {
        dispatch(loadPlaylist());
    }, [dispatch]);

    // Save player preferences only on app close - avoids blocking during usage
    useEffect(() => {
        const saveOnClose = () => {
            try {
                localStorage.setItem('musicPlayer', JSON.stringify({
                    playMode,
                    mode,
                    currentPlayId: 0,
                }));
            } catch (e) {
                // Silently fail
            }
        };

        window.addEventListener('beforeunload', saveOnClose);
        return () => window.removeEventListener('beforeunload', saveOnClose);
    }, [playMode, mode]);

    // Set initial player mode on mount
    useEffect(() => {
        handleSetPlayerMode(mode);
    }, [mode]);


    // Keep audioList ref in sync
    React.useEffect(() => {
        audioListRef.current = audioList;
    }, [audioList]);

    React.useEffect(() => {
        autoPlayRef.current = autoPlay;
    }, [autoPlay]);

    // ── Native (Capacitor) source-of-truth bridge ──────────────────────
    //
    // The native plugin (AVPlayer / ExoPlayer) owns the queue, the
    // position, and the auto-advance. JS hands it the whole queue via
    // setQueue, then receives a unified playerState event (4Hz while
    // playing + every transition + a resume snapshot on
    // didBecomeActive/handleOnResume). This is the bridge layer:
    //   1. audioList → engine.setQueue
    //   2. playerState → Redux setNativePlayerState
    //   3. appStateChange:isActive → getState reconcile (belt+suspenders
    //      behind the native-emitted resume snapshot)
    //
    // jinke stays mounted and renders the UI as usual; its muted HTML5
    // audio runs in parallel but contributes nothing audible. When
    // native auto-advances during background, the playerState index
    // change is what tells jinke to silently swap its UI index (via
    // updatePlayIndex when available, falling back to next-button click).

    // THE NATIVE QUEUE IS THE VISIBLE SURFACE.
    //
    // This used to push jinke's `audioList`, which on native holds exactly ONE
    // entry: the track the user tapped. Everything after it reached the plugin
    // one item at a time via the lock-screen refill, so the plugin's index
    // space and the surface's had nothing to do with each other. That mismatch
    // produced, repeatedly: a silently skipped track, "next" from row 2
    // landing on row 7, a row tap toggling a different track, and finally prev
    // refusing to go past the tapped row — with a one-item queue there is
    // nothing before it.
    //
    // Pushing the whole surface makes the plugin's index the surface's index:
    // prev is index-1 (5 → 4 → 3, whether or not you heard 4), next is
    // index+1, and the refill only matters past the end of the list.
    const nativeQueueRef = useRef([]);
    const lastNativeQueueSigRef = useRef('');
    // Position we last asked the plugin to play. listContext.currentIndex only
    // moves when the user taps a row, so a change here means a deliberate
    // pick — as opposed to this effect re-running for any other reason, which
    // must NOT reposition playback.
    const lastDesiredIndexRef = useRef(-1);
    // Previous audioList reference, so the effect can tell a row tap from any
    // other reason it re-ran.
    const lastAudioListRef = useRef(null);
    const lastListContextRef = useRef(null);
    // Where each "Add to Next Up" extra was anchored at add time (extra url →
    // anchor url). Owned by spliceNextUpExtras; entries for extras that left
    // audioList are dropped on every run.
    const nextUpAnchorsRef = useRef({});
    React.useEffect(() => {
        if (!isNative) return;
        const engine = getNativePlaybackEngine();
        if (!engine.isAvailable()) return;

        // Did this run come from a row tap?
        //
        // A tap replaces audioList AND sets a listContext, and playSnippet
        // dispatches them in that order. When React does not batch the two,
        // this effect runs twice: first with the new audioList but the OLD
        // listContext (so `desired` still points at the previously tapped
        // row), then with the new listContext but an unchanged audioList.
        // Keying only off audioList made run 1 reposition to the previous row
        // and run 2 decline to correct it — tap a row, land on the one you
        // tapped before. Intermittent, because it depends on batching.
        //
        // Either reference moving means the user picked something. Neither
        // moves on auto-advance, which is the case that must NOT reposition.
        const audioListChanged = audioList !== lastAudioListRef.current
            || listContext !== lastListContextRef.current;
        lastAudioListRef.current = audioList;
        lastListContextRef.current = listContext;

        const ctx = listContext;
        const base = (ctx?.type === 'tracks' && Array.isArray(ctx.items) && ctx.items.length)
            ? buildNativeQueue(ctx.items, ctx.currentIndex)
            : { entries: [], startIndex: 0 };
        let entries = base.entries;
        let desired = base.startIndex;

        if (!entries.length) {
            // No track surface (single-track play, a mixes surface, likes
            // playback) — fall back to jinke's queue so the tapped track still
            // reaches the plugin.
            entries = (audioList || []).filter(t => t && t.musicSrc);
            desired = 0;
        } else {
            // "Add to Next Up" entries live only in audioList. Splice them in
            // after the track the plugin was playing WHEN THEY WERE ADDED so
            // they keep the play-next priority the desktop's onAudioEnded
            // gives them — `desired` still points at the last tapped row,
            // which the plugin may have advanced far past, and anchoring
            // there buried the add behind the playhead (it never played).
            // Anchors persist across runs (see spliceNextUpExtras).
            // audioList[0] is the play-now seed (resetAudioList + addTrack);
            // everything after it is an explicit add. An add is OFTEN a track
            // that is also in the surface — on native, adding a feed track
            // queues the same sample url the surface entry uses — so a pure
            // url dedup ("already in the queue somewhere") silently dropped
            // the add and Next played the surface order (user report
            // 2026-08-21). Splice known-url adds too, under a deterministic
            // `nu=<index>` QUERY param so the url-keyed queue stays
            // unambiguous. A `#nu` fragment does NOT work here: Android's
            // native data source sends the fragment to the server and Apple's
            // CDN 404s → stream error → the add got error-skipped (verified
            // in logcat on-device). The CDN ignores unknown query params
            // (verified: 206 with ?nu=1), and /lazy-audio ignores them too.
            // The reducer's own `#qN` uniquifier (double-add of one track) has
            // the same Android problem, so any fragment is rewritten to the
            // query form as well.
            //
            // ONLY the `nextUp` stamp (set by addToNextUp) marks an explicit
            // add. "Anything after audioList[0]" is NOT a valid heuristic:
            // the advance path mechanically appends each next track to
            // audioList as it plays, and treating those as adds spliced
            // phantom duplicates next to the playhead (caught by the
            // on-device E2E).
            const known = new Set(entries.map(t => t.musicSrc));
            const extras = (audioList || [])
                .map((t, i) => ({ t, i }))
                .filter(({ t }) => t?.musicSrc && (t.nextUp === true || !known.has(t.musicSrc)))
                .map(({ t, i }) => {
                    let src = t.musicSrc;
                    const hash = src.indexOf('#');
                    if (hash >= 0) {
                        const base = src.slice(0, hash);
                        src = `${base}${base.includes('?') ? '&' : '?'}nu=${encodeURIComponent(src.slice(hash + 1))}`;
                    } else if (known.has(src)) {
                        src = `${src}${src.includes('?') ? '&' : '?'}nu=${i}`;
                    }
                    return src === t.musicSrc ? t : { ...t, musicSrc: src };
                });
            const spliced = spliceNextUpExtras(
                entries, extras, nativeAudioUrlRef.current, desired, nextUpAnchorsRef.current
            );
            entries = spliced.entries;
            desired = spliced.desired;
            nextUpAnchorsRef.current = spliced.anchors;
        }

        const prevSig = lastNativeQueueSigRef.current;
        const sig = queueSignature(entries);
        const sigUnchanged = sig === prevSig;
        if (!sigUnchanged) lastNativeQueueSigRef.current = sig;
        nativeQueueRef.current = entries;

        if (entries.length === 0) {
            if (!sigUnchanged) {
                engine.stop();
                nativeAudioUrlRef.current = null;
                lastDesiredIndexRef.current = -1;
            }
            return;
        }

        // Same surface as the last push. With the whole surface queued this is
        // the NORMAL case for a row tap: the queue is identical and only the
        // position changed. Act only when the position actually moved —
        // otherwise a re-render for any other reason would yank playback back
        // to the last tapped row, undoing a walk through prev.
        if (sigUnchanged) {
            // Reposition ONLY when a tap actually happened.
            //
            // `desired` comes from listContext.currentIndex, which stays on
            // the row the user last tapped while the plugin auto-advances past
            // it. So "the wanted track is not the one playing" is true for the
            // whole rest of the surface, and any unrelated re-run of this
            // effect would drag playback back to that row: a track ends,
            // something re-renders, and you land on the track BEFORE the one
            // that just finished.
            //
            // A tap replaces audioList (resetAudioList + addTrack), and
            // nothing else does — so a new audioList reference IS the signal
            // that the user picked something. Auto-advance leaves it alone.
            const target = entries[desired]?.musicSrc || null;
            if (audioListChanged && autoPlayRef.current && target
                && target !== nativeAudioUrlRef.current) {
                lastDesiredIndexRef.current = desired;
                nativeAudioUrlRef.current = target;
                // A deliberate start stands the phantom-pause guard down:
                // jinke's muted element re-fires 'play' on the new list, and
                // with userPausedRef still true from an earlier pause (plus a
                // touch older than the ~1.5s window — upgrades/extraction
                // easily outlast it) onAudioPlay paused the track ~200ms in.
                // Observed live on-device 2026-08-14.
                userPausedRef.current = false;
                engine.playIndex(desired);
            }
            return;
        }

        // Start where the audio ALREADY is when the queue merely grew or
        // shrank around it (a feed page-in appends, an Add-to-Next-Up splices
        // one entry in, a queue-panel delete removes one) — but a wholesale
        // replacement that happens to contain the playing track (a playlist
        // Play All, a row tap on a different surface) is a deliberate restart.
        // resolveQueueStartIndex tells the two apart by membership.
        const startIndex = resolveQueueStartIndex(prevSig, entries, nativeAudioUrlRef.current, desired);
        // Keep-position won over the asked-for start → this push merely grew
        // or shrank the queue around the loaded item (Add-to-Next-Up, feed
        // page-in, panel delete). Playback state must be left alone: forcing
        // playIndex here restarted the loaded track at 0:00 (and un-paused a
        // paused one) on every queue edit.
        const keptPosition = startIndex !== desired
            && entries[startIndex]?.musicSrc === nativeAudioUrlRef.current;
        lastDesiredIndexRef.current = desired;

        engine.setQueue({
            items: entries.map(t => ({
                url: t.musicSrc,
                title: t.name || '',
                artist: t.singer || '',
                // The THUMBNAIL, deliberately — the plugin upscales this
                // itself for the lock screen (see hiResArtwork in the native
                // plugins). Sending the upscaled url from here also made it
                // the url the plugin reports back in playerState, which the
                // reducer writes to currentTrack.cover, which is what the
                // now-playing hero renders — so the hero switched to a url
                // nothing had fetched and showed the PREVIOUS track's cover
                // until it downloaded. The surface that renders at 512px is
                // the one that should ask for 512px.
                artworkUrl: resolveCover(t.cover) || '',
            })),
            startIndex,
        }).then(() => {
            // setQueue LOADS without playing, and the plugin reports the url as
            // soon as it is loaded — so a "same url" test would decide playback
            // had started when nothing was playing. Ask whether it is actually
            // playing.
            const target = entries[startIndex]?.musicSrc || null;
            if (autoPlayRef.current && target && !keptPosition
                && (target !== nativeAudioUrlRef.current || !nativeIsPlayingRef.current)) {
                nativeAudioUrlRef.current = target;
                // Deliberate start — disarm the phantom-pause guard (see the
                // sig-unchanged branch above for the on-device failure).
                userPausedRef.current = false;
                engine.playIndex(startIndex);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioList, listContext, autoPlay]);

    // Native: keep the cover's spin honest.
    //
    // The play/pause hijack takes the click, so jinke never sees a pause and
    // its internal `playing` — which is what applies `.img-rotate` — stays
    // true for the life of the track. The artwork kept spinning after the
    // user paused. Redux mirrors the plugin at 4Hz, so drive it from there;
    // the CSS lives beside the rotation rules in MusicPlayer.scss.
    React.useEffect(() => {
        if (!isNative) return undefined;
        const root = document.querySelector('.react-jinke-music-player-main');
        if (!root) return undefined;
        root.classList.toggle('mg-native-paused', !reduxIsPlaying);
        return undefined;
    }, [reduxIsPlaying]);

    // Subscribe once to the native playerState stream and forward to
    // Redux. Also detects native auto-advance (index changed without JS
    // asking) and silently syncs jinke's UI so the queue/progress reflect
    // the audio that's actually playing on lock-screen-driven advance.
    React.useEffect(() => {
        if (!isNative) return undefined;
        const engine = getNativePlaybackEngine();
        if (!engine.isAvailable()) return undefined;

        // Lock-screen / notification / Bluetooth transport.
        //
        // This used to be wired lazily inside jinke's onAudioPlay, because at
        // mount time the plugin was not registered yet. That made a lock-screen
        // control depend on jinke having fired a play event — so any change
        // that stopped jinke from seeing a click (which is exactly what the
        // native play/pause hijack now does, deliberately) silently took the
        // lock screen with it. This effect already runs only when the engine
        // is available, so it is the honest place for it. hybridWiredRef keeps
        // it idempotent against the old call site.
        if (!hybridWiredRef.current) {
            hybridWiredRef.current = true;
            engine.on('remoteCommand', ({ action }) => {
                // The plugin has already actuated its own player — this only
                // mirrors the result into JS. Keep jinke's silent element in
                // step so its progress/queue UI doesn't drift.
                const el = audioInstanceRef.current;
                if (action === 'play') {
                    userPausedRef.current = false;
                    nativeIsPlayingRef.current = true;
                    if (el) el.play().catch(() => {});
                    dispatch(setAutoPlay(true));
                    dispatch(setIsPlaying(true));
                } else if (action === 'pause') {
                    userPausedRef.current = true;
                    nativeIsPlayingRef.current = false;
                    if (el) el.pause();
                    dispatch(setAutoPlay(false));
                    dispatch(setIsPlaying(false));
                }
            });
        }

        const offState = engine.on('playerState', (snap) => {
            if (!snap || typeof snap !== 'object') return;
            dispatch(setNativePlayerState(snap));

            // Keep our idea of "what the plugin is playing" honest. The
            // plugin now reports the loaded item's URL directly, so this is
            // correct even for tracks it auto-advanced or refilled into —
            // which the index-into-audioList lookup below can never resolve,
            // because audioList holds a single entry on native. Without this
            // the ref stayed pinned to the first track the user ever tapped,
            // and every guard that compares against it drew a wrong
            // conclusion from then on.
            if (typeof snap.url === 'string' && snap.url) {
                nativeAudioUrlRef.current = snap.url;
            }
            if (typeof snap.isPlaying === 'boolean') {
                nativeIsPlayingRef.current = snap.isPlaying;
            }

            // Keep currentTrackInfo on the track the PLUGIN is playing.
            //
            // It is otherwise only written by jinke's onAudioPlay, which fires
            // for jinke's own element — and that element never follows a
            // native auto-advance. So it stayed pinned to the originally
            // tapped track while NativeLockScreenRefill patched the VISIBLE
            // title to the current one: the player showed one track and its
            // like button was bound to another. Liking the playing track in
            // the feed left the player's heart empty.
            //
            // The plugin supplies title/artist/artwork/url; the catalog id
            // comes from matching that url back to the surface, which is the
            // only trustworthy key (title+artist collides across remixes).
            if (snap.url && snap.url !== lastInfoUrlRef.current) {
                lastInfoUrlRef.current = snap.url;
                const queued = (nativeQueueRef.current || [])
                    .find(t => t && t.musicSrc === snap.url) || null;
                const ctxItem = queued || (listContextRef.current?.items || [])
                    .find(t => t && sampleUrlOf(t) === snap.url) || null;
                setCurrentTrackInfo(prev => ({
                    ...(prev || {}),
                    name: snap.title || ctxItem?.name || ctxItem?.title || '',
                    singer: snap.artist || ctxItem?.singer || ctxItem?.artist || '',
                    // The SURFACE's cover first, and the plugin's only as a
                    // fallback — deliberately the opposite of title/artist.
                    //
                    // The plugin is handed the upscaled url (it draws the OS
                    // lock screen at ~512px). Letting that become
                    // currentTrack.cover made NowPlayingPage's hero point at a
                    // url nothing had fetched, which collapsed its
                    // thumbnail-first render — rawCover and heroCover became
                    // the same string, so the "show the cached one now,
                    // sharpen when it lands" path did nothing and the cover
                    // blanked on every track change. The surface's cover is
                    // the thumbnail the feed already painted, so it is in
                    // cache. Tracks that arrived by lock-screen refill have no
                    // surface entry and still fall through to the plugin's.
                    cover: ctxItem?.cover || ctxItem?.artwork || snap.artworkUrl || '',
                    musicSrc: snap.url,
                    samplePreviewUrl: snap.url,
                    trackId: queued ? queued.trackId : (ctxItem ? entityId(ctxItem) : null),
                    // Native plays samples only — no stream provenance, and
                    // never a mix.
                    originalUrl: null,
                    isMix: false,
                    mixId: null,
                }));
            }

            // Broadcast track changes and play/pause flips to row UIs. On
            // native this stream is the ONLY honest source: jinke's muted
            // element never follows the plugin's auto-advance (it sits
            // permanently paused, see the play/pause hijack), so without this
            // the play/pause indicator stayed pinned to the first/tapped row
            // for the rest of the session. Same event contract as jinke's
            // onAudioPlay broadcast (which is desktop/web-only now).
            const playingNow = snap.isPlaying === true;
            const lastB = lastNativeRowBroadcastRef.current;
            if (snap.url && (snap.url !== lastB.url || playingNow !== lastB.playing)) {
                lastNativeRowBroadcastRef.current = { url: snap.url, playing: playingNow };
                const queuedB = (nativeQueueRef.current || [])
                    .find(t => t && t.musicSrc === snap.url) || null;
                const ctxItemB = queuedB || (listContextRef.current?.items || [])
                    .find(t => t && sampleUrlOf(t) === snap.url) || null;
                const tid = queuedB ? queuedB.trackId : (ctxItemB ? entityId(ctxItemB) : null);
                window.__currentPlayerMusicSrc = snap.url;
                window.__currentPlayerOriginalUrl = null;
                window.__currentPlayerTrackId = tid != null ? String(tid) : null;
                window.__currentPlayerMixId = null;
                window.dispatchEvent(new CustomEvent('player-track-change', {
                    detail: {
                        musicSrc: snap.url,
                        originalUrl: null,
                        trackId: window.__currentPlayerTrackId,
                        mixId: null,
                        playing: playingNow,
                    },
                }));
            }

            // Silently sync jinke's UI index to the track the plugin is
            // playing WHEN that track exists in jinke's (tiny) list — matched
            // BY URL. Two hard-won rules here (both violated by the previous
            // version, found via the on-device E2E 2026-08-21):
            //
            //   1. `snap.index` is a position in the PLUGIN queue (the whole
            //      surface, ~100 items) — it must never index audioList
            //      (play-now + Next-Up adds, 1-2 items). Doing so picked an
            //      arbitrary entry and OVERWROTE nativeAudioUrlRef with its
            //      url, corrupting every later same-url guard (snap.url at
            //      the top of this handler is the only honest source).
            //   2. NEVER "sync" by clicking .next-audio. The button is
            //      hijacked: every click calls engine.nextTrack() and jinke
            //      never advances, so the old bounded-fallback loop fired all
            //      50 safety clicks and marched the REAL player through the
            //      queue. If updatePlayIndex is missing, do nothing — the
            //      visible UI is driven by the DOM patch + Redux anyway.
            {
                const list = audioListRef.current || [];
                const inst = audioInstanceRef.current;
                if (snap.url && inst && typeof inst.updatePlayIndex === 'function') {
                    const stripNu = (u) => String(u || '').replace(/[?&]nu=[^&]*$/, '');
                    const bare = stripNu(snap.url);
                    const idx = list.findIndex(t => t && (t.musicSrc === snap.url || stripNu(t.musicSrc) === bare));
                    if (idx >= 0) {
                        try { inst.updatePlayIndex(idx); } catch (e) { console.warn('[MusicPlayer] jinke sync failed', e); }
                    }
                }
            }

            // Position sync: write native's clock onto the muted jinke
            // audio element so the progress bar reads correctly after a
            // long background suspension (WKWebView's currentTime
            // freezes — we have to re-prime it). The suppress flag is
            // set here, then consumed by the 'seeked' handler itself
            // (see handleNativeSeek above) — that's the only way to
            // reliably skip the bounceback regardless of seeked-event
            // timing in WKWebView.
            try {
                const inst = audioInstanceRef.current;
                if (inst && typeof snap.positionSec === 'number') {
                    const delta = Math.abs((inst.currentTime || 0) - snap.positionSec);
                    if (delta > 1.5) {
                        suppressNativeSeekRef.current = true;
                        inst.currentTime = snap.positionSec;
                    }
                }
            } catch (_) { /* writes to a muted element are safe; ignore failures */ }
        });

        // MixGoatAudioPlugin emits 'error' on every native playback failure
        // (AVPlayer/ExoPlayer item error, dead URL, decode failure) and
        // NOTHING subscribed to it — the engine forwarded the event into the
        // void, so on mobile a failed track was silent in every sense: no
        // toast, no advance, no log. The web/Electron side of this is
        // onAudioError; this is its native counterpart.
        //
        // Deliberately NOT calling resetStuckPlayer: on native the plugin owns
        // the queue, so the recovery is to move on to the next item rather
        // than to empty jinke's list (which would stop nothing and clear the
        // UI's idea of what's playing).
        const offError = engine.on('error', (err) => {
            const name = currentTrackInfoRef.current?.name || 'this track';
            console.warn('[MusicPlayer] native playback error', err);
            const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
            toast.error(offline
                ? `Can’t play ${name} — you’re offline. Reconnect and try again.`
                : `Couldn’t play ${name} — skipping.`);
            trackEvent('playback_native_error', {
                code: err?.code != null ? String(err.code) : undefined,
                offline,
            });
            // Let the plugin advance; it owns the queue and knows what's next.
            // `nextTrack`, not `next` — the latter doesn't exist on the engine
            // and would have made this handler a silent no-op.
            try { engine.nextTrack?.(); } catch (_) { /* no next — playback just stops */ }
        });

        // Row/queue UIs (PlaylistSidebar rows, CustomPlaylistPanel) jump the
        // player by URL. Their desktop route — clicking jinke's hidden
        // audio-item DOM — is a silent no-op on native (the muted element
        // drives nothing), and they cannot know the plugin's index space.
        // Resolve the URL against the queue mirror here, where the refs live.
        const onPlayUrl = (e) => {
            const url = e?.detail?.url;
            if (!url) return;
            if (url === nativeAudioUrlRef.current) {
                // Same track: resume if paused, leave it alone if playing —
                // playIndex would restart it from the top.
                if (!nativeIsPlayingRef.current) {
                    userPausedRef.current = false;
                    nativeIsPlayingRef.current = true;
                    engine.play();
                    dispatch(setAutoPlay(true));
                    dispatch(setIsPlaying(true));
                }
                return;
            }
            const idx = (nativeQueueRef.current || []).findIndex(t => t && t.musicSrc === url);
            if (idx < 0) {
                console.warn('[MusicPlayer] native play-url not in queue', url);
                return;
            }
            userPausedRef.current = false;
            nativeAudioUrlRef.current = url;
            nativeIsPlayingRef.current = true;
            engine.playIndex(idx);
            dispatch(setAutoPlay(true));
            dispatch(setIsPlaying(true));
        };
        window.addEventListener('mixgoat-native-play-url', onPlayUrl);

        return () => {
            offState();
            offError();
            window.removeEventListener('mixgoat-native-play-url', onPlayUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Belt+suspenders resume reconcile. Native already emits a
    // playerState snapshot on didBecomeActive/handleOnResume; this
    // re-pulls via getState() in case the buffered event was dropped
    // (Capacitor #6234).
    React.useEffect(() => {
        if (!isNative) return undefined;
        const App = window.Capacitor?.Plugins?.App;
        if (!App || typeof App.addListener !== 'function') return undefined;
        let handle = null;
        const pending = App.addListener('appStateChange', async ({ isActive }) => {
            if (!isActive) return;
            const engine = getNativePlaybackEngine();
            if (!engine.isAvailable()) return;
            const snap = await engine.getState();
            if (snap && typeof snap === 'object' && snap.index >= 0) {
                dispatch(setNativePlayerState(snap));
            }
        });
        handle = pending;
        return () => {
            if (handle) Promise.resolve(handle).then(h => { try { h.remove(); } catch (_) {} });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Spin the bottom-player's play button while a track is extracting (yt-dlp
    // running). DJPalAudioPreview / MixPlayer dispatch the events around their
    // extractStreamUrl calls. The class goes on <body> not on the library's
    // own element — React's reconciliation overwrites className on re-render
    // for elements it owns, so we'd lose the class. Body is React-free.
    //
    // One continuous spinner for a whole track change: Next/Previous (or a
    // track ending) → extraction → jinke's buffering → audio actually playing.
    // Dropping it at extract-end showed jinke's play icon in the gaps (between
    // the click and extract-start, and between extract-end and buffering) —
    // play / spinner / play / pause flashing. It now holds until the player's
    // audio fires `playing` (or errors), with a timeout so it never spins on.
    useEffect(() => {
        let activeExtractions = 0;
        let holdTimer = null;
        const setSpin = (on) => {
            if (on) document.body.classList.add('extracting-track');
            else document.body.classList.remove('extracting-track');
        };
        const release = () => {
            clearTimeout(holdTimer); holdTimer = null;
            if (activeExtractions === 0) setSpin(false);
        };
        const hold = (ms) => {
            setSpin(true);
            clearTimeout(holdTimer);
            holdTimer = setTimeout(release, ms);
        };
        const isPlayerAudio = (t) => t && t === window.musicPlayerInstance;
        const onStart = () => {
            activeExtractions++;
            setSpin(true);
        };
        // Previous on a track past its start just restarts it: no new source,
        // so no `playing` — that one case may end on the audio advancing.
        let restartCheck = false;
        let lastPlayingAt = 0;
        const onEnd = () => {
            activeExtractions = Math.max(0, activeExtractions - 1);
            if (activeExtractions > 0) return;
            // Some paths report the end only after the new track started.
            if (Date.now() - lastPlayingAt < 1500) release();
            // Otherwise the new source is set right after: bridge to it.
            else hold(2500);
        };
        const onLoadStart = (e) => {
            if (!isPlayerAudio(e.target)) return;
            restartCheck = false;
            if (document.body.classList.contains('extracting-track')) hold(10000);
        };
        const onPlaying = (e) => { if (isPlayerAudio(e.target)) { lastPlayingAt = Date.now(); restartCheck = false; release(); } };
        const onTime = (e) => {
            if (restartCheck && isPlayerAudio(e.target) && !e.target.paused && e.target.readyState >= 3) { restartCheck = false; release(); }
        };
        const onError = (e) => { if (isPlayerAudio(e.target)) { activeExtractions = 0; release(); } };
        // Auto-advance: a short hold bridges to the advance's own extract-start.
        const onEnded = (e) => { if (isPlayerAudio(e.target)) hold(4000); };
        const onSkipClick = (e) => {
            const btn = e.target?.closest?.('.react-jinke-music-player-main .next-audio, .react-jinke-music-player-main .prev-audio');
            if (!btn) return;
            restartCheck = btn.classList.contains('prev-audio');
            hold(6000);
        };
        window.addEventListener('djpal-track-extract-start', onStart);
        window.addEventListener('djpal-track-extract-end', onEnd);
        // Media events don't bubble; capture-phase listeners still see them.
        document.addEventListener('loadstart', onLoadStart, true);
        document.addEventListener('playing', onPlaying, true);
        document.addEventListener('error', onError, true);
        document.addEventListener('ended', onEnded, true);
        document.addEventListener('timeupdate', onTime, true);
        document.addEventListener('click', onSkipClick, true);
        return () => {
            window.removeEventListener('djpal-track-extract-start', onStart);
            window.removeEventListener('djpal-track-extract-end', onEnd);
            document.removeEventListener('loadstart', onLoadStart, true);
            document.removeEventListener('playing', onPlaying, true);
            document.removeEventListener('error', onError, true);
            document.removeEventListener('ended', onEnded, true);
            document.removeEventListener('timeupdate', onTime, true);
            document.removeEventListener('click', onSkipClick, true);
            clearTimeout(holdTimer);
            setSpin(false);
        };
    }, []);

    // Drive the bottom mini-player's play/pause ICON from Redux on native.
    // jinke's own .play-btn icon reflects its MUTED <audio> element, which on
    // iOS WKWebView stops when its (single, looping-disabled) sample reaches
    // ~30s — so after a native auto-advance jinke shows a ▶ even though the
    // AVPlayer is still playing the next track. We mark <body> with the real
    // play state (same body-class trick as the extracting spinner — React
    // strips classList on its own elements) and override the icon in CSS.
    // VISUAL ONLY: this touches no audio element, so it cannot affect playback
    // (the loop approach that did was reverted). The button's click still goes
    // to jinke.
    useEffect(() => {
        if (!isNative) return undefined;
        document.body.classList.add('mg-native-player');
        if (reduxIsPlaying) document.body.classList.add('mg-native-playing');
        else document.body.classList.remove('mg-native-playing');
        return () => {
            document.body.classList.remove('mg-native-player');
            document.body.classList.remove('mg-native-playing');
        };
    }, [reduxIsPlaying]);

    // Hijack jinke's playlist / reload / next buttons via MutationObserver
    // instead of polling.
    //
    // The library re-renders these buttons (track changes, mode changes, etc.)
    // and we need to re-attach our handlers each time. Previously this was
    // three separate `setInterval(..., 500)` loops running constantly — which
    // queued up missed ticks while the window was backgrounded and fired in
    // a burst on refocus, contributing to the multi-second refocus freeze.
    //
    // MutationObserver fires only on actual DOM changes inside the player and
    // we rAF-coalesce so a burst of jinke mutations (slider drag, time tick)
    // collapses to one hijack pass per frame. All three hijacks are
    // idempotent (dataset flag) so the cost of an unnecessary pass is three
    // querySelector calls.
    //
    // Handlers are routed through refs so this effect's deps stay empty —
    // means the observer doesn't tear down + rebuild every render, which
    // also means no risk of leaving stale capture listeners attached.
    //
    // jinke history note: each button is REPLACED (cloneNode) for the
    // playlist and reload cases to drop the library's own handlers; the
    // next-audio button is left in place and we just attach in capture
    // phase, so our handler runs before the library's stop-on-end logic.
    // Don't change this without a real reason — fights with the library
    // have been expensive in past PRs.
    const autoRadioRef = React.useRef(autoRadio);
    autoRadioRef.current = autoRadio;

    const playlistClickRef = React.useRef(null);
    const reloadClickRef = React.useRef(null);
    const nextClickRef = React.useRef(null);
    const prevClickRef = React.useRef(null);
    const playClickRef = React.useRef(null);

    // Native only, and ONLY when jinke has nothing loaded.
    //
    // iOS jettisons the WKWebView content process during a long lock while
    // keeping the app (and AVPlayer) alive. On resume the web view reloads
    // from scratch: React remounts, Redux is back to initial state, and
    // `audioList` is empty (musicPlayer is not in PERSISTED_SLICES). The
    // plugin meanwhile still holds its queue and a loaded item, so the
    // mini-player renders a real track at a real position — and its play
    // button, which drives jinke, is attached to nothing. Tapping it did
    // nothing at all.
    //
    // When jinke has no source, the plugin is the only thing that can
    // answer. When it DOES have one, this is a no-op and the existing
    // onAudioPlay/onAudioPause path runs exactly as before — that path
    // owns analytics and the phantom-resume guards, so we must not take
    // it over in the normal case.
    playClickRef.current = (e) => {
        if (!isNative) return;
        const engine = getNativePlaybackEngine();
        if (!engine.isAvailable()) return;
        // Take the click. jinke must NEVER see it on native.
        //
        // Its element is muted and permanently paused here — the plugin is the
        // thing making sound — so `jinke.state.playing` is always false. Its
        // toggle therefore reads every click as "start playing" and calls
        // loadAndPlayAudio(), which sets `loading: true` and only clears it
        // when the element actually fires `play`. That never happens on
        // native. jinke then renders `.loading-icon` in place of `.play-btn`
        // — and NowPlayingPage's transport proxies to `.play-btn` by
        // selector, so the user's play button silently became a no-op after
        // a single pause. Measured on-device: one pause click leaves
        // {playing: false, loading: true} forever with `.play-btn` gone.
        //
        // An earlier attempt at this (a3d639bc) was reverted for "spinner, no
        // sound", and the comment left behind blamed preventDefault for the
        // spinner. That was the wrong culprit: back then onAudioPlay was still
        // what drove the plugin, so suppressing the click killed the audio,
        // and the spinner came from the track-change path rather than from
        // this handler. Now that the plugin is driven from right here, taking
        // the event is both safe and the fix.
        if (e) { e.preventDefault(); e.stopPropagation(); }
        const pausing = !!reduxIsPlaying;
        userPausedRef.current = pausing;
        if (pausing) engine.pause(); else engine.play();
        // Update the play-state mirror NOW, not when the plugin's next
        // playerState arrives ~250ms later. engine.play() is async, and jinke
        // plays its own element on the same click — so onAudioPlay fires in
        // between, carrying the element's long-stale src. With the mirror
        // still reading "not playing", that looked like a genuine track
        // change and jumped the plugin to that stale track's index, i.e. the
        // first track of the surface. We know the intent here; say so
        // immediately and the phantom is correctly ignored.
        nativeIsPlayingRef.current = !pausing;
        dispatch(setAutoPlay(!pausing));
        dispatch(setIsPlaying(!pausing));
        // onAudioPause no longer runs on native, so the row-icon broadcast it
        // owned lives here — otherwise the bottom player reads paused while
        // every feed row still shows playing.
        window.dispatchEvent(new CustomEvent('player-track-change', {
            detail: {
                musicSrc: window.__currentPlayerMusicSrc || null,
                originalUrl: window.__currentPlayerOriginalUrl || null,
                trackId: window.__currentPlayerTrackId || null,
                playing: !pausing,
            },
        }));
    };

    // Native only — the mirror of the next-button fix below. jinke's list
    // holds a single track there, so its own prev button can only ever
    // restart that one; the plugin is the side that knows what came
    // before. On desktop this handler is never attached and the library
    // keeps its default behavior.
    prevClickRef.current = (e) => {
        if (!isNative) return;
        const engine = getNativePlaybackEngine();
        if (!engine.isAvailable()) return;
        e.stopPropagation();
        e.preventDefault();
        engine.prevTrack();
    };

    playlistClickRef.current = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setCustomPanelOpen((prev) => !prev);
    };

    reloadClickRef.current = (e) => {
        e.stopPropagation();
        e.preventDefault();
        dispatch(toggleAutoRadio());
        const willBeOn = !autoRadioRef.current;
        toast.info(willBeOn ? 'Auto-discover on' : 'Auto-discover off');
    };

    useEffect(() => {
        // Hide the library's panel permanently via CSS (we render our own).
        const style = document.createElement('style');
        style.textContent = '.react-jinke-music-player-main .audio-lists-panel { display: none !important; }';
        document.head.appendChild(style);

        // Stable wrappers — the observer effect runs with [] deps but the
        // closure inside each ref captures fresh state on every render.
        const onPlaylistClick = (e) => playlistClickRef.current && playlistClickRef.current(e);
        const onReloadClick = (e) => reloadClickRef.current && reloadClickRef.current(e);
        const onNextClick = (e) => nextClickRef.current && nextClickRef.current(e);
        const onPrevClick = (e) => prevClickRef.current && prevClickRef.current(e);
        const onPlayClick = (e) => playClickRef.current && playClickRef.current(e);

        // Track the next/prev buttons we last attached to so cleanup can drop
        // the capture listeners cleanly. Earlier buttons are unreachable
        // (jinke replaced them) so they GC with their listeners.
        let lastNextBtn = null;
        let lastPrevBtn = null;
        let lastPlayBtn = null;

        const reloadIcon = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.585 0-4.585 8 0 8 5.606 0 7.644-8 12.74-8z"/></svg>';

        const hijackAll = () => {
            // 1. Playlist button — clone (drops library listeners) + open our custom panel.
            const playlistBtn = document.querySelector('.react-jinke-music-player-main .player-content .audio-lists-btn');
            if (playlistBtn && !playlistBtn.dataset.hijacked) {
                const clone = playlistBtn.cloneNode(true);
                playlistBtn.parentNode.replaceChild(clone, playlistBtn);
                clone.dataset.hijacked = 'true';
                clone.addEventListener('click', onPlaylistClick);
            }

            // 2. Reload button — clone + repaint icon as infinity + toggle auto-radio.
            const reloadBtn = document.querySelector('.react-jinke-music-player-main .player-content .reload-btn');
            if (reloadBtn && !reloadBtn.dataset.hijacked) {
                const clone = reloadBtn.cloneNode(true);
                reloadBtn.parentNode.replaceChild(clone, reloadBtn);
                clone.dataset.hijacked = 'true';
                clone.innerHTML = reloadIcon;
                clone.addEventListener('click', onReloadClick);
            }

            // 3. Next-audio button — capture-phase listener, no clone (we WANT
            // jinke's default to run if our handler doesn't preventDefault).
            const nextBtn = document.querySelector('.react-jinke-music-player-main .player-content .next-audio');
            if (nextBtn && !nextBtn.dataset.radioHijacked) {
                nextBtn.dataset.radioHijacked = 'true';
                nextBtn.addEventListener('click', onNextClick, true);
                lastNextBtn = nextBtn;
            }

            // 4. Prev-audio button — NATIVE ONLY, so desktop/web keep the
            // library's own prev behavior untouched. Same capture-phase,
            // no-clone pattern as next.
            if (isNative) {
                const prevBtn = document.querySelector('.react-jinke-music-player-main .player-content .prev-audio');
                if (prevBtn && !prevBtn.dataset.nativeHijacked) {
                    prevBtn.dataset.nativeHijacked = 'true';
                    prevBtn.addEventListener('click', onPrevClick, true);
                    lastPrevBtn = prevBtn;
                }

                // 5. Play/pause — NATIVE ONLY, and the handler itself bails
                // out unless jinke has no source (see playClickRef). Always
                // attached so it is there the moment it IS needed; attaching
                // it costs one querySelector per hijack pass.
                const playBtn = document.querySelector('.react-jinke-music-player-main .player-content .play-btn');
                if (playBtn && !playBtn.dataset.nativeHijacked) {
                    playBtn.dataset.nativeHijacked = 'true';
                    playBtn.addEventListener('click', onPlayClick, true);
                    lastPlayBtn = playBtn;
                }
            }
        };

        // Coalesce mutation bursts (slider drag, time tick) to one pass per frame.
        let scheduled = false;
        const scheduledHijack = () => {
            if (scheduled) return;
            scheduled = true;
            requestAnimationFrame(() => { scheduled = false; hijackAll(); });
        };

        // Find player root, then observe it. If not in DOM yet, briefly
        // observe body for it to appear (disconnects as soon as found).
        let inner = null;
        let outer = null;
        let cancelled = false;

        const tryAttach = () => {
            if (cancelled || inner) return true;
            const root = document.querySelector('.react-jinke-music-player-main');
            if (!root) return false;
            hijackAll();
            inner = new MutationObserver(scheduledHijack);
            inner.observe(root, { childList: true, subtree: true });
            if (outer) { outer.disconnect(); outer = null; }
            return true;
        };

        if (!tryAttach()) {
            outer = new MutationObserver(tryAttach);
            outer.observe(document.body, { childList: true, subtree: true });
        }

        return () => {
            cancelled = true;
            if (inner) inner.disconnect();
            if (outer) outer.disconnect();
            try { document.head.removeChild(style); } catch {}
            if (lastNextBtn && lastNextBtn.isConnected) {
                lastNextBtn.removeEventListener('click', onNextClick, true);
                try { delete lastNextBtn.dataset.radioHijacked; } catch {}
            }
            if (lastPrevBtn && lastPrevBtn.isConnected) {
                lastPrevBtn.removeEventListener('click', onPrevClick, true);
                try { delete lastPrevBtn.dataset.nativeHijacked; } catch {}
            }
            if (lastPlayBtn && lastPlayBtn.isConnected) {
                lastPlayBtn.removeEventListener('click', onPlayClick, true);
                try { delete lastPlayBtn.dataset.nativeHijacked; } catch {}
            }
        };
    }, []); // intentionally [] — click handlers are routed through refs so they pick up fresh state without rebuilding the observer

    // Define the actual next-button click logic. Updated every render via ref
    // — see the observer effect above for why this is a ref instead of being
    // attached directly. Logic is unchanged from the previous setInterval
    // implementation.
    nextClickRef.current = (e) => {
        // Native FIRST — even during likes playback. The likes early-return
        // below exists for jinke's own advance on desktop; on native it let
        // jinke's default next advance only the muted mirror element, so the
        // UI moved to the next track while the plugin kept playing the old
        // one (mobile likes "next does nothing", 2026-08-20). The likes queue
        // lives in the plugin via the setQueue effect, so nextTrack() is the
        // correct advance for it too.
        //
        // Native: the plugin owns the queue AND its own position, so skip
        // is one call to it.
        //
        // The path below is wrong on native and visibly so: it steps from
        // `listContext.currentIndex`, which NOTHING advances there —
        // onAudioEnded early-returns on native, so the index stays pinned
        // to whatever the user originally tapped. Once the plugin has
        // auto-advanced a few tracks, "next" resolved an item BEHIND the
        // one playing. Reported as: open the track page, hit skip, and it
        // jumps to the previous track.
        if (isNative) {
            const engine = getNativePlaybackEngine();
            if (engine.isAvailable()) {
                e.stopPropagation();
                e.preventDefault();
                engine.nextTrack();
                return;
            }
        }

        // Desktop likes playback: jinke advances through the pre-queued likes
        // list itself — the listContext-stepping below must not interfere.
        if (likesPlaybackRef.current) return;

        const player = window.musicPlayerInstance;
        if (!player) return;

        const list = audioListRef.current || [];
        const currentSrc = player.src || '';
        const currentIdx = list.findIndex(t => currentSrc.includes(encodeURIComponent(t.musicSrc)) || currentSrc === t.musicSrc);
        const isLast = currentIdx >= list.length - 1;

        // If jinke can naturally advance to the next queued track, let it.
        if (!isLast || list.length === 0) return;

        // List context: queue the next playable item and play it.
        const ctx = listContextRef.current;
        if (ctx?.type && ctx.items?.length && ctx.currentIndex < ctx.items.length - 1) {
            e.stopPropagation();
            e.preventDefault();
            if (!listContextAdvancingRef.current) advanceListContext();
            return;
        }

        // Auto-radio: list exhausted (or never set) → seed next from current track.
        if (autoRadioRef.current) {
            e.stopPropagation();
            e.preventDefault();
            const trackInfo = currentTrackInfoRef.current;
            if (trackInfo?.name) {
                fetchRadioRecommendations(trackInfo.name, trackInfo.singer, radioSeedsFromContext(), { userGesture: true });
            }
        }
    };

    // Keep refs current for the next-button hijack
    const likesPlaybackRef = React.useRef(likesPlayback);
    likesPlaybackRef.current = likesPlayback;

    // Make jinke's .audio-title clickable in non-waveform mode (the waveform
    // overlay renders its own clickable title). Re-runs when track changes
    // because jinke re-renders the audio-title node.
    //
    // Mobile (≤768px): tapping the title expands the NowPlayingPage —
    // matches Spotify/Apple Music behaviour where the mini-player title
    // promotes you to the full-screen player. Desktop keeps the existing
    // "open in Mixpedia" behaviour, which is the natural shortcut to the
    // track's detail page in the desktop layout.
    useEffect(() => {
        if (showWaveform && mode === 'full') return; // waveform overlay covers it
        const audioTitle = document.querySelector('.react-jinke-music-player-main .audio-title');
        if (!audioTitle) return;
        const isMobileViewport = () =>
            typeof window !== 'undefined' && window.matchMedia?.('(max-width: 768px)')?.matches;
        audioTitle.classList.add('mixpedia-clickable');
        audioTitle.setAttribute('title', isMobileViewport() ? 'Open Now Playing' : 'Open in Mixpedia');
        const onClick = (e) => {
            e.stopPropagation();
            if (isMobileViewport()) {
                dispatch(setExpanded(true));
            } else {
                handleSearchInMixpedia();
            }
        };
        audioTitle.addEventListener('click', onClick);
        return () => {
            audioTitle.removeEventListener('click', onClick);
            audioTitle.classList.remove('mixpedia-clickable');
            audioTitle.removeAttribute('title');
        };
    }, [showWaveform, mode, currentTrackInfo, handleSearchInMixpedia, dispatch]);

    // Update reload button styling when autoRadio changes
    useEffect(() => {
        const reloadBtn = document.querySelector('.react-jinke-music-player-main .player-content .reload-btn');
        if (!reloadBtn) return;
        reloadBtn.setAttribute('title', autoRadio ? 'Auto-discover: ON' : 'Auto-discover: OFF');
        if (autoRadio) {
            reloadBtn.classList.add('auto-radio-active');
        } else {
            reloadBtn.classList.remove('auto-radio-active');
        }
    }, [autoRadio]);


    // Inject toggle button, waveform, and save button containers into player's DOM structure
    useEffect(() => {
        const panelContent = document.querySelector('.react-jinke-music-player-main .panel-content');
        const playerContent = document.querySelector('.react-jinke-music-player-main .player-content');
        const progressBarContent = document.querySelector('.react-jinke-music-player-main .progress-bar-content');

        if (!panelContent || !playerContent || !progressBarContent) {
            return;
        }

        // Create toggle button container (goes before player-content)
        let toggleContainer = document.getElementById('waveform-toggle-container');
        if (!toggleContainer) {
            toggleContainer = document.createElement('div');
            toggleContainer.id = 'waveform-toggle-container';
            panelContent.insertBefore(toggleContainer, playerContent);
        }


        // Create waveform container (goes in place of progress-bar-content)
        let waveformContainer = document.getElementById('waveform-replacement-container');
        if (!waveformContainer) {
            waveformContainer = document.createElement('div');
            waveformContainer.id = 'waveform-replacement-container';
            panelContent.insertBefore(waveformContainer, progressBarContent);
        }

        setPortalReady(true);

        return () => {
            setPortalReady(false);
            if (toggleContainer && toggleContainer.parentNode) {
                toggleContainer.parentNode.removeChild(toggleContainer);
            }
            if (waveformContainer && waveformContainer.parentNode) {
                waveformContainer.parentNode.removeChild(waveformContainer);
            }
        };
        // Only re-run when player mode flips or when the player transitions
        // between empty/non-empty. Queue-size changes must NOT tear down the
        // waveform container — that causes a portal remount flash on resume.
    }, [mode, audioList.length > 0]); // eslint-disable-line react-hooks/exhaustive-deps

    // Inject save button into playlist panel header (separate effect since it might not exist initially)
    useEffect(() => {
        const injectSaveButton = () => {
            const headerActions = document.querySelector('.react-jinke-music-player-main .audio-lists-panel-header-actions');

            if (headerActions) {
                let saveButtonContainer = document.getElementById('save-playlist-button-container');
                if (!saveButtonContainer) {
                    saveButtonContainer = document.createElement('div');
                    saveButtonContainer.id = 'save-playlist-button-container';
                    saveButtonContainer.style.display = 'inline-block';
                    // Insert before the delete button (first child)
                    headerActions.insertBefore(saveButtonContainer, headerActions.firstChild);
                }
            }
        };

        // Try immediately
        injectSaveButton();

        // Also try after short delays in case the panel isn't rendered yet
        const timer1 = setTimeout(injectSaveButton, 100);
        const timer2 = setTimeout(injectSaveButton, 300);
        const timer3 = setTimeout(injectSaveButton, 500);

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
        };
    }, [mode, audioList.length]);

    // Update playlist title in player header
    useEffect(() => {
        const updatePlaylistTitle = () => {
            const titleSpan = document.querySelector('.react-jinke-music-player-main .audio-lists-panel-header-title > span:first-child');
            const numSpan = document.querySelector('.react-jinke-music-player-main .audio-lists-panel-header-num');

            if (titleSpan) {
                if (selectedPlaylistName) {
                    titleSpan.textContent = selectedPlaylistName + ' ';
                } else {
                    titleSpan.textContent = 'Playlists ';
                }
            }

            if (numSpan) {
                const trackCount = audioList.length;
                numSpan.textContent = `(${trackCount} track${trackCount !== 1 ? 's' : ''})`;
            }

            // Also update the player bar counter
            const barNum = document.querySelector('.react-jinke-music-player-main .player-content .audio-lists-btn .audio-lists-num');
            if (barNum) {
                barNum.textContent = audioList.length;
            }
        };

        // Try immediately
        updatePlaylistTitle();

        // Also try after delays in case the panel isn't rendered yet
        const timer1 = setTimeout(updatePlaylistTitle, 100);
        const timer2 = setTimeout(updatePlaylistTitle, 300);
        const timer3 = setTimeout(updatePlaylistTitle, 500);

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
        };
    }, [selectedPlaylistName, audioList.length, mode]);

    // Smooth crossfade transition between progress bar and waveform
    useEffect(() => {
        const progressBar = document.querySelector('.react-jinke-music-player-main .progress-bar');
        const audioTitle = document.querySelector('.react-jinke-music-player-main .audio-title');
        const audioMain = document.querySelector('.react-jinke-music-player-main .audio-main');

        // Add smooth transitions
        if (progressBar) progressBar.style.transition = 'opacity 0.3s ease, visibility 0.3s ease';
        if (audioTitle) audioTitle.style.transition = 'opacity 0.3s ease, visibility 0.3s ease';
        if (audioMain) audioMain.style.transition = 'opacity 0.3s ease, visibility 0.3s ease';

        // Only hide if waveform is active AND player is in full mode
        if (showWaveform && mode === 'full') {
            // Fade out progress bar
            if (progressBar) {
                progressBar.style.opacity = '0';
                setTimeout(() => {
                    progressBar.style.visibility = 'hidden';
                    progressBar.style.pointerEvents = 'none';
                }, 300);
            }
            // Fade out title and times
            if (audioTitle) {
                audioTitle.style.opacity = '0';
                setTimeout(() => audioTitle.style.visibility = 'hidden', 300);
            }
            if (audioMain) {
                audioMain.style.opacity = '0';
                setTimeout(() => audioMain.style.visibility = 'hidden', 300);
            }
        } else {
            // Fade in progress bar
            if (progressBar) {
                progressBar.style.visibility = '';
                progressBar.style.pointerEvents = '';
                setTimeout(() => progressBar.style.opacity = '', 10);
            }
            if (audioTitle) {
                audioTitle.style.visibility = '';
                setTimeout(() => audioTitle.style.opacity = '', 10);
            }
            if (audioMain) {
                audioMain.style.visibility = '';
                setTimeout(() => audioMain.style.opacity = '', 10);
            }
        }
    }, [showWaveform, mode]);

    const handleSetPlayerMode = (mode) => {
        const tabContainers = document.querySelectorAll('.audio-main');
        tabContainers.forEach((tabContainer) => {
            if (mode === 'full') {
                tabContainer.classList.add('reduced-height');
            } else {
                tabContainer.classList.remove('reduced-height');
            }
        });
        // .player-full-mode on .app-container is applied by App.js based on Redux
        // state — don't toggle it imperatively here or React's next render will
        // overwrite it when unrelated state (e.g. sidebar open) changes.
    };

    // A /lazy-audio search resolved a link for a queue entry added without
    // one: patch the entry + current-track info so the waveform overlay gets
    // a real peaks source and the For You row can match the playing track.
    useEffect(() => {
        const off = window.electron?.djpal?.onLazyAudioResolved?.((p) => {
            const tid = p?.tid;
            const youtubeId = p?.youtubeId;
            if (!tid || !youtubeId) return;
            const watchUrl = `https://www.youtube.com/watch?v=${youtubeId}`;
            const marker = `tid=${encodeURIComponent(tid)}`;
            dispatch(upgradeTrackLink({ marker, youtubeId, originalUrl: watchUrl }));
            // FeedWaveform rows key their peaks source off this event.
            try { window.dispatchEvent(new CustomEvent('djpal-track-link-resolved', { detail: { trackId: String(tid), youtubeId } })); } catch (_) { /* best-effort */ }
            setCurrentTrackInfo((prev) => {
                if (!prev?.musicSrc?.includes(marker)) return prev;
                if (window.__currentPlayerMusicSrc === prev.musicSrc) {
                    window.__currentPlayerOriginalUrl = watchUrl;
                    window.__currentPlayerTrackId = String(tid);
                    window.__currentPlayerMixId = null; // lazy-audio entries are tracks
                    try {
                        window.dispatchEvent(new CustomEvent('player-track-change', {
                            detail: { musicSrc: prev.musicSrc, originalUrl: watchUrl, trackId: String(tid), mixId: null, playing: !window.musicPlayerInstance?.paused },
                        }));
                    } catch (_) { /* best-effort */ }
                }
                return { ...prev, youtubeId, originalUrl: watchUrl };
            });
        });
        return () => { try { off?.(); } catch (_) { /* noop */ } };
    }, [dispatch]);

    // /lazy-audio fell back to the SAMPLE for the playing entry: broadcast a
    // track-change keyed by the SAMPLE URL so the For You row (which matches
    // on its sample src) activates and draws the sample waveform + progress.
    useEffect(() => {
        const off = window.electron?.djpal?.onLazyAudioFallback?.((p) => {
            const fallback = p?.fallback;
            if (!fallback) return;
            try {
                window.dispatchEvent(new CustomEvent('player-track-change', {
                    detail: { musicSrc: fallback, originalUrl: null, playing: !window.musicPlayerInstance?.paused },
                }));
            } catch (_) { /* best-effort */ }
            // WaveformOverlay keys off this: a /lazy-audio entry that 302'd to
            // its Apple sample has no originalUrl to fetch peaks for, so the
            // player's waveform stayed a blank seek strip forever. The overlay
            // recognizes the fallback by its own `fallback=` query param and
            // decodes the sample clip instead. The Set replays the answer for
            // overlays that mount after the event fired (first track of a
            // queue resolves before its overlay exists).
            try {
                (window.__lazyAudioFallbacks = window.__lazyAudioFallbacks || new Set()).add(fallback);
                window.dispatchEvent(new CustomEvent('lazy-audio-fallback', { detail: { fallback } }));
            } catch (_) { /* best-effort */ }
        });
        return () => { try { off?.(); } catch (_) { /* noop */ } };
    }, []);

    const handleAudioListsChange = (currentPlayId, audioLists, audioInfo) => {
        // Additions flow app → jinke only (jinke's non-clear update path is
        // append-only). REMOVALS flow jinke → app: the queue panel routes
        // deletes through jinke's own delete (so its internal list updates and
        // playback advances correctly), and this mirror keeps Redux aligned —
        // the two lists diverging is what made panel clicks hit ghost tracks.
        const reduxList = audioListRef.current || [];
        if (Array.isArray(audioLists) && audioLists.length < reduxList.length) {
            // Count-aware (multiset) reconcile: a plain Set wiped BOTH copies
            // of a duplicated track when one instance was deleted.
            const counts = new Map();
            for (const t of audioLists) counts.set(t?.musicSrc, (counts.get(t?.musicSrc) || 0) + 1);
            const reconciled = [];
            for (const t of reduxList) {
                const c = counts.get(t.musicSrc) || 0;
                if (c > 0) { counts.set(t.musicSrc, c - 1); reconciled.push(t); }
            }
            if (reconciled.length !== reduxList.length) {
                dispatch(syncQueueFromPlayer(reconciled));
            }
        }
    };

    const handleSaveToPlaylist = async (e) => {
        e.stopPropagation();
        e.preventDefault();

        // If a playlist is already loaded, update it directly
        if (selectedPlaylistId && selectedPlaylistName) {
            try {
                // Get file paths from audio list
                const filePaths = audioList.map(item => {
                    try {
                        const url = new URL(item.musicSrc);
                        const pathParam = url.searchParams.get('path');
                        return pathParam ? decodeURIComponent(pathParam) : null;
                    } catch (error) {
                        console.error('Error parsing musicSrc URL:', item.musicSrc, error);
                        return null;
                    }
                }).filter(path => path !== null);

                // Clear existing tracks and add new ones
                await window.electron.db.playlists.clear(selectedPlaylistId);
                await window.electron.db.playlists.addTracks(selectedPlaylistId, filePaths);

                // Trigger playlist sidebar to refresh
                dispatch(triggerPlaylistRefresh());

                toast.success(`Saved ${filePaths.length} track${filePaths.length !== 1 ? 's' : ''} to "${selectedPlaylistName}"`);
            } catch (error) {
                console.error('Error updating playlist:', error);
                toast.error(`Failed to update playlist: ${errMsg(error)}`);
            }
        } else {
            // No playlist selected, show modal to create new one
            dispatch(setShowSavePlayerModal(true));
        }
    };

    // Keep currentTrackInfo in a ref so radio callback can access latest value
    React.useEffect(() => {
        currentTrackInfoRef.current = currentTrackInfo;
    }, [currentTrackInfo]);

    React.useEffect(() => {
        listContextRef.current = listContext;
    }, [listContext]);

    // Prefetch the NEXT context item's audio once the current one is clearly
    // being listened to, so advanceListContext hits warm caches on BOTH skip
    // and natural end (measured gap: ~5-7s cold vs ~0.7s warm; ~half of all
    // streaming transitions are skips, and most skips land after the first
    // 10s — Spotify MSSD research). Two triggers, either arms it:
    //   ≥10s CUMULATIVE listened (from the analytics play session, which
    //     accumulates across seeks and pause/resume) — the engagement gate.
    //     A DJ auditioning sections (2s intro + 3s verse + 5s drop) crosses
    //     it; sub-10s touch-and-leave row-hopping fires NOTHING, so audition
    //     bursts add zero YouTube requests — that burst is exactly where
    //     403/throttle windows start, so this gate is load-bearing.
    //     Deliberately NOT playhead position: seeking to 2:30 is not 150s
    //     of engagement.
    //   remaining ≤ 45s — the endgame catch for seek-to-the-outro skims and
    //     short items (30s samples arm immediately — correct, they end
    //     within 30s). 45s covers the worst measured throttle-window
    //     download (~25s) with margin.
    // Tracks only — mixes stream (no download to warm) and local files need
    // nothing. Downstream is deduped and non-priority, so worst case is
    // exactly the old cold-gap behavior.
    const prefetchedForIndexRef = useRef(null);
    const prefetchCtxSeenRef = useRef({ key: null, at: 0 });
    React.useEffect(() => {
        if (!reduxIsPlaying || !(duration > 0)) return;
        const ctx = listContext;
        let next = ctx?.items?.[ctx.currentIndex + 1];
        let key = next ? `${ctx.type}:${ctx.currentIndex}` : null;
        // No list context (Play All on a playlist, "Next Up" queues, liked
        // tracks): the QUEUE is the list. Its entries are /lazy-audio URLs
        // that download the full file only when the element asks for them,
        // and /lazy-audio is download-first — so without this every queue
        // transition was a cold 15-25s download gap while For You's (list-
        // context) transitions were warm. Same gate, same downstream.
        let queueNext = null;
        if (!next) {
            const q = audioListRef.current || [];
            const cur = currentTrackInfoRef.current?.musicSrc;
            const i = cur ? q.findIndex((t) => t?.musicSrc === cur) : -1;
            const cand = i >= 0 ? q[i + 1] : null;
            // Only entries that resolve to a downloadable full stream: skip
            // local files, mixes (HLS/streamed) and bare sample URLs.
            const src = String(cand?.musicSrc || '');
            const isLazyOrLinked = src.includes('/lazy-audio')
                || (cand?.youtubeId && cand.youtubeId !== '-1')
                || !!cand?.soundcloudUrl;
            const isLocalOrMix = src.includes('/audio?path=') || cand?.isHls || cand?.hlsSrc || cand?.mixId;
            if (cand && isLazyOrLinked && !isLocalOrMix) {
                queueNext = cand;
                key = `queue:${cur}`;
            }
        }
        if (!next && !queueNext) return;
        // Settle guard: on a context switch, currentTime/duration still hold
        // the PREVIOUS track's position for a few hundred ms (until the new
        // element's first timeupdate) — the old position can spuriously
        // satisfy the gate and fire the prefetch at second 0 of the new
        // track. Ignore this context's first 1.5s; the timeupdate-driven
        // re-runs re-evaluate with real values right after.
        if (prefetchCtxSeenRef.current.key !== key) {
            prefetchCtxSeenRef.current = { key, at: Date.now() };
            return;
        }
        if (Date.now() - prefetchCtxSeenRef.current.at < 1500) return;
        const sess = playSessionRef.current;
        const listenedMs = sess && !sess.completed
            ? sess.listenMs + (sess.resumedAt ? Date.now() - sess.resumedAt : 0)
            : 0;
        if (listenedMs < 10000 && duration - currentTime > 45) return;
        if (queueNext) {
            if (prefetchedForIndexRef.current === key) return;
            prefetchedForIndexRef.current = key;
            prefetchTrackAudio(queueNext);
            return;
        }
        const nextType = next._type === 'mix' ? 'mixes'
            : next._type === 'track' ? 'tracks'
            : ctx.type;
        if (nextType !== 'tracks') return;
        if (prefetchedForIndexRef.current === key) return;
        prefetchedForIndexRef.current = key;
        prefetchTrackAudio(next);
    }, [reduxIsPlaying, currentTime, duration, listContext]);

    // Advance to the next item in the current list context.
    // Tries each subsequent item in order, skipping those with no resolvable audio.
    // Forward ref to fetchRadioRecommendations — defined after advanceListContext
    // but called from inside it when the list runs dry under infinity mode.
    const fetchRadioRecommendationsRef = useRef(null);

    const advanceListContext = React.useCallback(async (opts = {}) => {
        // fromError: we got here because the CURRENT track failed to load (not
        // because it finished). If nothing playable remains, the failed track
        // is still sitting in jinke spinning its loader forever — clear it.
        const fromError = opts.fromError === true;
        if (listContextAdvancingRef.current) {
            console.warn('[MusicPlayer] advanceListContext skipped — already advancing');
            return;
        }
        listContextAdvancingRef.current = true;
        // Spin the bottom-player play-icon while we resolve + extract the
        // next track. DJPalAudioPreview fires the same start/end events
        // around its own click → extract flow, and MusicPlayer's CSS
        // toggles the spinner on these events. Mirror the pattern here so
        // the user gets visual feedback during the next-button resolve
        // chain (which can take 1-3s for cached YT, 5-15s when the lazy
        // search has to run).
        window.dispatchEvent(new CustomEvent('djpal-track-extract-start'));

        const ctx = listContextRef.current;
        if (!ctx?.type || !ctx.items?.length) {
            console.warn('[MusicPlayer] advanceListContext: no list context', { type: ctx?.type, items: ctx?.items?.length });
            listContextAdvancingRef.current = false;
            window.dispatchEvent(new CustomEvent('djpal-track-extract-end'));
            return;
        }
        console.warn('[MusicPlayer] advanceListContext entry', { type: ctx.type, currentIndex: ctx.currentIndex, itemsLength: ctx.items.length });

        try {
            for (let i = ctx.currentIndex + 1; i < ctx.items.length; i++) {
                const item = ctx.items[i];
                let resolved = null;
                // The musicSrc this iteration queues — the retry loop below
                // verifies THIS src loaded (robust to duplicate-src items).
                let queuedSrc = null;

                // Per-item type wins over ctx.type — supports mixed lists (e.g. likes
                // in "all" mode) where each entry carries its own _type marker.
                const itemType = item?._type === 'mix' ? 'mixes'
                    : item?._type === 'track' ? 'tracks'
                    : ctx.type;

                if (itemType === 'tracks') {
                    // Tell the matching feed row to start its waveform loader
                    // NOW — before the (1-15s) resolve+extract below. Matches the
                    // manual click path, where djpal-row-play starts the loader
                    // immediately. FeedWaveform matches on sample src / trackId.
                    window.dispatchEvent(new CustomEvent('feed-row-preload', {
                        detail: {
                            src: item.previewUrl || item.sampleUrl || item.preview_url || item.preview || null,
                            trackId: entityId(item),
                        },
                    }));
                    // Hard cap: a hung resolve (stalled lazy IPC / yt-dlp 403
                    // retry storm) left the player spinner up FOREVER and
                    // blocked the advance loop. Timeout → skip to next item.
                    resolved = await Promise.race([
                        resolveTrackSrc(item),
                        new Promise((r) => setTimeout(() => r(null), 30000)),
                    ]);
                    if (!resolved) {
                        console.warn('[MusicPlayer] advanceListContext: track unresolved, skipping', { i, title: item?.title, artist: item?.artist });
                        continue;
                    }

                    dispatch(setListContextIndex(i));
                    queuedSrc = resolved.musicSrc;
                    dispatch(addTrackToQueue({
                        name: item.title || 'Unknown Track',
                        singer: item.artist || '',
                        cover: resolveCover(item.artwork, item.artworkUrl, item.image, item.cover),
                        musicSrc: resolved.musicSrc,
                        // Carry the catalog id like every manual play path does —
                        // without it, the player-bar "open in Mixpedia" for an
                        // auto-advanced track had no trackId and fell to text.
                        trackId: entityId(item),
                        ...(resolved.isHls ? { isHls: true, hlsSrc: resolved.hlsSrc } : {}),
                        ...(resolved.originalUrl ? { originalUrl: resolved.originalUrl } : {}),
                        ...(Array.isArray(item.artists) && item.artists.length ? { artists: item.artists } : {}),
                    }));
                } else if (itemType === 'mixes') {
                    // Same hard cap as tracks — mixes extract streams too.
                    resolved = await Promise.race([
                        resolveMixSrc(item),
                        new Promise((r) => setTimeout(() => r(null), 45000)),
                    ]);
                    if (!resolved) {
                        console.warn('[MusicPlayer] advanceListContext: mix unresolved, skipping', { i, title: item?.title });
                        continue;
                    }

                    dispatch(setListContextIndex(i));
                    const mixName = item.date
                        ? `${item.title || 'Untitled Mix'} (${item.date})`
                        : (item.title || 'Untitled Mix');
                    queuedSrc = resolved.musicSrc;
                    dispatch(addTrackToQueue({
                        name: mixName,
                        singer: item.djName || item.dj_name || item.artist || '',
                        cover: resolveCover(resolved.thumbnail, item.artwork, item.artworkUrl, item.image, item.cover),
                        musicSrc: resolved.musicSrc,
                        // Use the resolved link (a lazy-recovery replacement may
                        // differ from item.link), falling back to the original.
                        originalUrl: resolved.originalUrl || item.link,
                        isMix: true,
                        isHls: resolved.isHls || false,
                        ...(resolved.isHls && resolved.hlsSrc ? { hlsSrc: resolved.hlsSrc } : {}),
                        mixId: entityId(item),
                        mixTitle: item.title || '',
                    }));
                } else if (itemType === 'local') {
                    // Local library file — resolve to the audio-server URL. No
                    // YT/SoundCloud lazy resolve; if the file is gone, skip it.
                    // getAudioUrl builds the URL blindly (no fs check), so ask
                    // the backend whether the file is actually on this machine —
                    // otherwise a synced-from-another-computer track would 404
                    // and bounce us back here through onAudioError.
                    try {
                        const missing = await window.electron.db.tracks.checkMissingFiles([item.filePath]);
                        if (Array.isArray(missing) && missing.length > 0) {
                            console.warn('[MusicPlayer] advanceListContext: local file not on this device, skipping', { i, filePath: item?.filePath });
                            reportMissingFile();
                            continue;
                        }
                    } catch (e) { /* check failed — fall through and try to play */ }
                    let musicSrc = null;
                    try { musicSrc = await window.electron.getAudioUrl(item.filePath); }
                    catch (e) { /* unreadable — skip */ }
                    if (!musicSrc) {
                        console.warn('[MusicPlayer] advanceListContext: local file unresolved, skipping', { i, filePath: item?.filePath });
                        continue;
                    }
                    dispatch(setListContextIndex(i));
                    queuedSrc = musicSrc;
                    dispatch(addTrackToQueue({
                        name: item.title || 'Unknown Track',
                        singer: item.artist || '',
                        cover: item.cover || '',
                        musicSrc,
                    }));
                }

                dispatch(setAutoPlay(true));
                // Click "next" until the player's src actually SWITCHES. A
                // single delayed click could miss (button not rendered yet /
                // jinke mid-update), and the only retry was jinke re-firing
                // `ended` after REPLAYING the whole track — on a real
                // 6-minute file that read as playback never continuing.
                // Guarded on src-change so a slow-buffering track is never
                // double-skipped: once src changed, no further clicks.
                {
                    const prevSrc = window.musicPlayerInstance ? window.musicPlayerInstance.src : null;
                    // With no instance to compare against, retries could never
                    // detect the switch and would click blindly — single shot.
                    const maxAttempts = prevSrc === null ? 0 : 8;
                    // Success = the EXPECTED queued src is loaded (works even
                    // when the next item duplicates the current src), or any
                    // other switch (a manual skip took over) — stop clicking.
                    const expectSrc = queuedSrc;
                    const switched = (src) => typeof src === 'string' && (
                        (expectSrc && (src === expectSrc || src.includes(encodeURIComponent(expectSrc))))
                        || (prevSrc !== null && src !== prevSrc));
                    const tryAdvanceClick = (attempt) => {
                        const audio = window.musicPlayerInstance;
                        if (attempt > 0 && audio && switched(audio.src)) return; // done (attempt 0 must click even on a duplicate src)
                        const nextBtn = document.querySelector('.react-jinke-music-player-main .player-content .next-audio');
                        if (nextBtn) nextBtn.click();
                        else if (attempt === 0) console.warn('[MusicPlayer] advanceListContext: .next-audio button not rendered yet', { i });
                        if (attempt < maxAttempts) setTimeout(() => tryAdvanceClick(attempt + 1), 700);
                        else if (maxAttempts > 0 && !(window.musicPlayerInstance && switched(window.musicPlayerInstance.src))) console.warn('[MusicPlayer] advanceListContext: playback did not switch after retries', { i });
                    };
                    setTimeout(() => tryAdvanceClick(0), 500);
                }
                return; // Successfully queued next item
            }

            // All remaining items exhausted — clear context. If infinity mode is
            // on, hand off to the radio flow seeded by the last-played track so
            // playback continues instead of just stopping.
            console.warn('[MusicPlayer] advanceListContext: all remaining items unresolved, clearing context');
            // Snapshot the seeds BEFORE clearing the context — after the clear
            // the fallback list is gone. Same catalog-first ordering as
            // radioSeedsFromContext (kept inline: that callback is declared
            // after this one).
            const ctxItems = (listContextRef.current?.items || []).slice().reverse();
            const inCat = (it) => !!(it.djpalId || it.trackId || it.id);
            const radioSeeds = [...ctxItems.filter(inCat), ...ctxItems.filter((it) => !inCat(it))].map((it) => ({
                name: it.title || it.name || '',
                singer: it.artist || it.djName || it.dj_name || it.singer || '',
            }));
            dispatch(clearListContext());
            if (autoRadioRef.current) {
                const trackInfo = currentTrackInfoRef.current;
                if (trackInfo?.name) {
                    fetchRadioRecommendationsRef.current?.(trackInfo.name, trackInfo.singer, radioSeeds);
                }
            } else if (fromError) {
                // The loaded track errored and there's nothing to advance to —
                // without this, jinke keeps its loading spinner going forever
                // on the dead source. Empty the queue so the player resets.
                console.warn('[MusicPlayer] advanceListContext: error path exhausted with nothing playable — clearing player');
                dispatch(setAutoPlay(false));
                dispatch(setAudioList([]));
            }
        } finally {
            listContextAdvancingRef.current = false;
            window.dispatchEvent(new CustomEvent('djpal-track-extract-end'));
        }
    }, [dispatch, reportMissingFile]);

    // Auto-radio: fetch recommendations when queue is about to end
    // Seed candidates for the radio hand-off, drawn from the ambient list the
    // user just played through: catalog-known tracks (djpalId from enrichment)
    // FIRST, nearest-to-the-end first within each group. A list often ends on
    // a run of album rips the recommendation API can't match — seeding purely
    // back-to-front burned every attempt on those and dead-ended the radio.
    const radioSeedsFromContext = React.useCallback(() => {
        const ctx = listContextRef.current;
        if (!ctx?.items?.length) return [];
        const reversed = [...ctx.items].reverse();
        const inCatalog = (it) => !!(it.djpalId || it.trackId || it.id);
        return [...reversed.filter(inCatalog), ...reversed.filter((it) => !inCatalog(it))].map((it) => ({
            name: it.title || it.name || '',
            singer: it.artist || it.djName || it.dj_name || it.singer || '',
        }));
    }, []);

    // Seed candidates for a radio lookup: the given track first, then the
    // list fallbacks, then tracks PLAYED earlier this session — if radio
    // found music before, a dead-end list must not strand playback.
    const buildRadioCandidates = React.useCallback((trackName, trackArtist, fallbackSeeds = []) => {
        const seenSeeds = new Set();
        const dedupSeeds = (arr) => arr
            .filter((c) => c && c.name)
            .filter((c) => {
                const k = `${(c.singer || '').toLowerCase()}|||${c.name.toLowerCase()}`;
                if (seenSeeds.has(k)) return false;
                seenSeeds.add(k);
                return true;
            });
        const playedFallback = [...playedTrackKeys].reverse().map((k) => {
            const i = k.indexOf('|||');
            return i >= 0 ? { singer: k.slice(0, i), name: k.slice(i + 3) } : null;
        });
        return [
            ...dedupSeeds([{ name: trackName, singer: trackArtist }, ...fallbackSeeds]).slice(0, 24),
            ...dedupSeeds(playedFallback).slice(0, 6),
        ];
    }, [playedTrackKeys]);

    // The slow part of the radio hand-off (seed walk + stream resolve), with
    // no side effects — shared by the on-ended path and the prefetch below.
    // Returns { track, resolved } or null.
    const findRadioTrack = React.useCallback(async (candidates) => {
        const token = localStorage.getItem('djpal:token');
        if (!token) { console.warn('[AutoRadio] No DJ Pal token'); return null; }
        const playedSet = new Set(playedTrackKeys);
        let fresh = [];
        for (const cand of candidates) {
            const singer = cand.singer && cand.singer !== 'Unknown Artist' ? cand.singer : '';
            const query = singer ? `${singer} - ${cand.name}` : cand.name;
            let result;
            try {
                result = await window.electron.djpal.searchTrackRecommendations(token, query, 'NODE2VEC', 'A');
            } catch (e) {
                console.warn('[AutoRadio] Recommendations request failed for seed:', query, e);
                continue;
            }
            if (result.error) {
                console.warn('[AutoRadio] Recommendations error for seed:', query, result.error);
                continue;
            }
            const data = result.data || result;
            const relatedTracks = (data.relatedTracks || []).map(r => r.track || r);
            const frequentlyPlayed = data.frequentlyPlayedTogether || [];
            // Filter out already played tracks
            fresh = [...relatedTracks, ...frequentlyPlayed].filter(t => {
                const key = `${t.artist}|||${t.title}`;
                return t.artist && t.title && !playedSet.has(key);
            });
            if (fresh.length > 0) break;
            console.warn('[AutoRadio] Seed came up dry, trying the previous list track:', query);
        }
        if (fresh.length === 0) {
            // Last tier: catalog-known tracks from the user's LIBRARY. A list
            // of personal productions/edits can't seed and a fresh session has
            // no played history (MG-1012: Windows, own tracks, cold start) —
            // the library is the remaining source of this user's taste.
            const tried = new Set(candidates.map((c) => `${(c.singer || '').toLowerCase()}|||${(c.name || '').toLowerCase()}`));
            let librarySeeds = [];
            try {
                const all = await window.electron?.db?.tracks?.getAll?.();
                librarySeeds = (all || [])
                    .map((r) => (r.track || r))
                    .filter((t) => t && t.djpalId && t.title && t.artist)
                    .map((t) => ({ singer: t.artist, name: t.title }))
                    .filter((c) => !tried.has(`${c.singer.toLowerCase()}|||${c.name.toLowerCase()}`))
                    .slice(-6)
                    .reverse();
            } catch (e) { /* no local DB (web) — skip the tier */ }
            for (const cand of librarySeeds) {
                const query = `${cand.singer} - ${cand.name}`;
                let result;
                try {
                    result = await window.electron.djpal.searchTrackRecommendations(token, query, 'NODE2VEC', 'A');
                } catch (e) { continue; }
                if (!result || result.error) continue;
                const data = result.data || result;
                fresh = [...((data.relatedTracks || []).map(r => r.track || r)), ...(data.frequentlyPlayedTogether || [])]
                    .filter(t => t.artist && t.title && !playedSet.has(`${t.artist}|||${t.title}`));
                if (fresh.length > 0) { console.warn('[AutoRadio] seeded from library track:', query); break; }
                console.warn('[AutoRadio] library seed came up dry:', query);
            }
        }
        if (fresh.length === 0) return null;
        // Resolve and queue just one track — next one loads when this ends.
        // Route through resolveTrackSrc so sample-only tracks (no DB
        // youtubeId / soundcloudUrl) still get a lazy YT-link resolve
        // attempt before being queued as a snippet. Walk the fresh list
        // until one RESOLVES — dying on the first unresolvable rec silently
        // killed the radio even when the seed returned good recommendations.
        for (const cand of fresh.slice(0, 5)) {
            const r = await resolveTrackSrc(cand);
            if (r) return { track: cand, resolved: r };
            console.warn('[AutoRadio] track unresolved, skipping', { title: cand?.title, artist: cand?.artist });
        }
        console.warn('[AutoRadio] no resolvable recommendation among', fresh.length);
        return null;
    }, [playedTrackKeys]);

    // Prefetched hand-off, keyed by the src of the (last) track it was
    // computed for. Consumed by fetchRadioRecommendations, armed by the
    // timeupdate effect further down.
    const radioPrefetchRef = React.useRef({ forSrc: null, promise: null, result: null });

    const fetchRadioRecommendations = React.useCallback(async (trackName, trackArtist, fallbackSeeds = [], { userGesture = false } = {}) => {
        if (radioFetchingRef.current) return;
        // Drop same-seed repeats from jinke's ended re-fires (see the ref's
        // comment). An explicit user gesture (next-button) is never
        // throttled — a dead next button reads as a broken player.
        const attemptKey = `${(trackArtist || '').toLowerCase()}|||${(trackName || '').toLowerCase()}`;
        if (!userGesture
            && lastRadioAttemptRef.current.key === attemptKey
            && Date.now() - lastRadioAttemptRef.current.at < RADIO_ATTEMPT_COOLDOWN_MS) return;
        radioFetchingRef.current = true;

        try {
            // Get DJ Pal token. Not stamped as an attempt — a retry right
            // after signing in must not be cooldown-suppressed.
            let token = localStorage.getItem('djpal:token');
            if (!token) {
                console.warn('[AutoRadio] No DJ Pal token');
                return;
            }
            lastRadioAttemptRef.current = { key: attemptKey, at: Date.now() };

            // Use the prefetched result when it was computed for the track
            // that just ended (armed ~25s before the last list track ends —
            // the walk+resolve otherwise leaves a 10-15s silent gap).
            let found = null;
            const pf = radioPrefetchRef.current;
            const endedSrc = window.musicPlayerInstance ? window.musicPlayerInstance.src : null;
            if (pf.forSrc && endedSrc && pf.forSrc === endedSrc) {
                found = pf.result || (pf.promise ? await pf.promise : null);
                if (found) console.warn('[AutoRadio] using prefetched hand-off');
            }
            radioPrefetchRef.current = { forSrc: null, promise: null, result: null };
            if (!found) found = await findRadioTrack(buildRadioCandidates(trackName, trackArtist, fallbackSeeds));
            if (!found) {
                toast.info("Couldn't find related tracks to continue — this music isn't in the Mixpedia catalog yet");
                return;
            }
            const { track, resolved } = found;

            const art = track.artwork || track.artworkUrl || track.image || '';
            dispatch(addTrackToQueue({
                name: track.title,
                singer: track.artist,
                cover: resolveCover(art),
                musicSrc: resolved.musicSrc,
                trackId: entityId(track),
                ...(resolved.isHls ? { isHls: true, hlsSrc: resolved.hlsSrc } : {}),
                ...(resolved.originalUrl ? { originalUrl: resolved.originalUrl } : {}),
            }));
            dispatch(addPlayedTrackKey(`${track.artist}|||${track.title}`));

            // Start playback — retry until the player's src switches (same
            // src-change-guarded loop as advanceListContext: a single missed
            // click left the queued rec sitting unplayed).
            {
                const prevSrc = window.musicPlayerInstance ? window.musicPlayerInstance.src : null;
                const maxAttempts = prevSrc === null ? 0 : 8;
                const expectSrc = resolved.musicSrc;
                const switched = (src) => typeof src === 'string' && (
                    (expectSrc && (src === expectSrc || src.includes(encodeURIComponent(expectSrc))))
                    || (prevSrc !== null && src !== prevSrc));
                const tryStartClick = (attempt) => {
                    const audio = window.musicPlayerInstance;
                    if (attempt > 0 && audio && switched(audio.src)) return;
                    const nextBtn = document.querySelector('.react-jinke-music-player-main .player-content .next-audio');
                    if (nextBtn) nextBtn.click();
                    if (attempt < maxAttempts) setTimeout(() => tryStartClick(attempt + 1), 700);
                    else if (maxAttempts > 0 && !(window.musicPlayerInstance && switched(window.musicPlayerInstance.src))) console.warn('[AutoRadio] playback did not switch to the queued recommendation');
                };
                setTimeout(() => { dispatch(setAutoPlay(true)); tryStartClick(0); }, 500);
            }
        } catch (error) {
            console.error('[AutoRadio] Error:', error);
        } finally {
            radioFetchingRef.current = false;
        }
    }, [dispatch, toast, buildRadioCandidates, findRadioTrack]);

    // Wire forward-ref so callbacks defined earlier (advanceListContext) can
    // invoke fetchRadioRecommendations without a TDZ-prone forward reference.
    fetchRadioRecommendationsRef.current = fetchRadioRecommendations;

    // ---- Radio hand-off prefetch ----
    // The end-of-list hand-off does a seed walk + stream resolve that can
    // take 10s+ (each dry seed is a server round-trip; the resolve may run a
    // lazy YT extract). Start it while the LAST list track is still playing
    // so the switch is near-instant — same idea as the feed's next-track
    // prefetch. Non-last tracks advance through the list and never need it.
    useEffect(() => {
        if (!audioElement || isNative) return undefined;
        const PREFETCH_LEAD_S = 25;
        const onTime = () => {
            try {
                if (!autoRadioRef.current || likesPlaybackRef.current) return;
                const ctx = listContextRef.current;
                if (!ctx?.items?.length || ctx.currentIndex < ctx.items.length - 1) return; // LAST item only
                const d = audioElement.duration;
                if (!isFinite(d) || d <= 0) return;
                if (d - (audioElement.currentTime || 0) > PREFETCH_LEAD_S) return;
                const src = audioElement.src;
                if (!src || radioPrefetchRef.current.forSrc === src) return; // already armed for this track
                const info = currentTrackInfoRef.current;
                if (!info?.name) return;
                console.warn('[AutoRadio] prefetching hand-off for list end');
                const candidates = buildRadioCandidates(info.name, info.singer, radioSeedsFromContext());
                const promise = findRadioTrack(candidates).then((res) => {
                    if (radioPrefetchRef.current.forSrc === src) radioPrefetchRef.current.result = res;
                    return res;
                }).catch(() => null);
                radioPrefetchRef.current = { forSrc: src, promise, result: null };
            } catch (e) { /* prefetch must never break playback */ }
        };
        audioElement.addEventListener('timeupdate', onTime);
        return () => audioElement.removeEventListener('timeupdate', onTime);
    }, [audioElement, buildRadioCandidates, findRadioTrack, radioSeedsFromContext]);

    // Fetch next batch of liked tracks for continuous likes playback
    const likesOffsetRef = React.useRef(likesPlaybackOffset);
    React.useEffect(() => { likesOffsetRef.current = likesPlaybackOffset; }, [likesPlaybackOffset]);
    const likesFetchingRef = React.useRef(false);

    const fetchNextLikesBatch = React.useCallback(async () => {
        if (likesFetchingRef.current) return;
        likesFetchingRef.current = true;

        try {
            const token = localStorage.getItem('auth:accessToken');
            if (!token) return;

            const offset = likesOffsetRef.current;
            const page = Math.floor(offset / 20) + 1;

            // Fetch both tracks and mixes. refreshAndRetry: a stale token
            // (expired mid-listen) refreshes and retries instead of failing.
            const [tracksResult, mixesResult] = await Promise.all([
                refreshAndRetry((t) => window.electron.library.getLikes(t, page, 20)),
                refreshAndRetry((t) => window.electron.library.getLikedMixes(t, page, 20)),
            ]);

            // Transient failure ({error}) is NOT "no more likes" — bail out
            // quietly and leave likes playback armed; the next track-end
            // retries this batch. Only a genuine empty page ends the run.
            if (tracksResult?.error || mixesResult?.error) {
                console.warn('[LikesPlayback] batch fetch failed, will retry:', tracksResult?.error || mixesResult?.error);
                return;
            }

            const tracks = tracksResult?.data || [];
            const mixes = mixesResult?.data || [];

            if (tracks.length === 0 && mixes.length === 0) {
                dispatch(setLikesPlayback(false));
                // If infinity (auto-radio) mode is on, hand off to the radio
                // flow seeded by the last-played track instead of just stopping.
                if (autoRadioRef.current) {
                    const trackInfo = currentTrackInfoRef.current;
                    if (trackInfo?.name) {
                        fetchRadioRecommendations(trackInfo.name, trackInfo.singer);
                    }
                } else {
                    toast.info('All liked items played');
                }
                return;
            }

            // Merge and sort by created_at
            const all = [
                ...tracks.map(t => ({ ...t, _type: 'track' })),
                ...mixes.map(m => ({ ...m, _type: 'mix' })),
            ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

            // Resolve stream URLs and queue. Route tracks through
            // resolveTrackSrc so sample-only items (preview_url with no
            // youtube_id / soundcloud_url) still get a lazy YT-link resolve
            // attempt before being queued as a snippet — otherwise jinke
            // plays the 30s sample and never tries to find full playback.
            for (const item of all) {
                // These are RAW user_likes rows (fetched directly, not the
                // boundary-normalized slice): `item.id` is the LIKE-ROW
                // integer — the catalog id lives in track_public_id. Never
                // pass item.id / entityId(item) as the entity id here.
                if (item._type === 'mix') {
                    const resolved = await resolveMixSrc({ link: item.link, playbackBlocked: item.playbackBlocked });
                    if (!resolved) continue;
                    dispatch(addTrackToQueue({
                        name: item.title,
                        singer: item.dj_name || '',
                        cover: resolveCover(item.artwork),
                        musicSrc: resolved.musicSrc,
                        isMix: true,
                        mixId: item.track_public_id || null,
                        mixTitle: item.title || '',
                        ...(resolved.isHls ? { isHls: true, hlsSrc: resolved.hlsSrc } : {}),
                        ...(resolved.originalUrl ? { originalUrl: resolved.originalUrl } : {}),
                    }));
                } else {
                    // `id:` override is load-bearing — resolveTrackSrc reads the
                    // entity id via entityId(), which on a raw row would pick up
                    // the like-row integer and lazy-resolve a YouTube link for a
                    // completely unrelated catalog track.
                    const resolved = await resolveTrackSrc({ ...item, id: item.track_public_id || null });
                    if (!resolved) continue;
                    dispatch(addTrackToQueue({
                        name: item.title,
                        singer: item.artist || '',
                        cover: resolveCover(item.artwork),
                        musicSrc: resolved.musicSrc,
                        trackId: item.track_public_id || null,
                        ...(resolved.isHls ? { isHls: true, hlsSrc: resolved.hlsSrc } : {}),
                        ...(resolved.originalUrl ? { originalUrl: resolved.originalUrl } : {}),
                    }));
                }
            }

            dispatch(setLikesPlaybackOffset(offset + 20));
        } catch (error) {
            console.error('[LikesPlayback] Error:', error);
        } finally {
            likesFetchingRef.current = false;
        }
    }, [dispatch, toast, fetchRadioRecommendations]);

    const getOptions = () => ({
        theme: 'dark',
        locale: 'en_US',
        responsive: false,
        showThemeSwitch: false,
        showLyric: false,
        showDownload: false,
        // Custom title renderer: on mobile show title + artist as two
        // stacked lines (Spotify/SoundCloud vibe). Desktop unchanged —
        // falls back to jinke's default "name - singer" single line.
        renderAudioTitle: (info) => {
            // Native: `info` is jinke's OWN list entry, and the plugin's
            // queue outgrows that list (refill-appended recs live only
            // native-side). jinke re-renders rewrote the title back to the
            // last list entry it knew, clobbering the DOM patch that had the
            // right name — reported as "a new track plays but the player
            // still names the previous one". Render from the playerState
            // mirror instead; it is the only source that always matches the
            // audio. Web/desktop keep jinke's info — there jinke IS the truth.
            const native = isNative ? nativeCurrentTrackRef.current : null;
            const shown = (native && native.name) ? native : info;
            const isNarrow = typeof window !== 'undefined'
                && window.matchMedia?.('(max-width: 768px)')?.matches;
            if (!isNarrow) {
                return `${shown?.name || ''}${shown?.singer ? ` - ${shown.singer}` : ''}`;
            }
            return (
                <span className="mixgoat-mini-title-stack">
                    <span className="mixgoat-mini-title-line">{shown?.name || ''}</span>
                    {shown?.singer && <span className="mixgoat-mini-artist-line">{shown.singer}</span>}
                </span>
            );
        },
        // preload="auto" — keep buffering bytes even when paused so the loader
        // can finish and resume is instant. Library default is "none", which
        // strands the loading spinner if you pause mid-load.
        preload: true,
        clearPriorAudioLists,
        playMode,
        autoPlay,
        mode,
        audioLists: audioList,
        defaultVolume: initialVolume,
        onAudioVolumeChange: (volume) => {
            // The library hands us `audio.volume`, which is `bar²` (it
            // applies a perceptual-loudness curve internally). `defaultVolume`
            // on the next mount is interpreted as the BAR value and squared
            // again — so storing audio.volume here causes the volume to drift
            // smaller on every restart. Save the bar value (sqrt) so the
            // restored `defaultVolume` round-trips cleanly.
            //
            // Write synchronously, no debounce — localStorage writes are
            // cheap and the previous 250ms debounce lost the last value
            // when the app was closed quickly after a volume change.
            const barValue = Math.sqrt(volume);
            try { localStorage.setItem('musicPlayer:volume', String(barValue)); } catch {}
            try { window.electron?.settings?.set?.('musicPlayer:volume', String(barValue)); } catch {}
        },
        onPlayModeChange: (newPlayMode) => {
            dispatch(setPlayMode(newPlayMode));
        },
        onModeChange: (newMode) => {
            dispatch(setMode(newMode));
            handleSetPlayerMode(newMode);
        },
        onAudioListsChange: (currentPlayId, audioLists, audioInfo) => {
            handleAudioListsChange(currentPlayId, audioLists, audioInfo)
            if (audioLists.length === 0) {
                // Delay clearing so track switches (resetAudioList + addTrack)
                // don't unmount WaveformOverlay mid-fetch
                clearTrackInfoTimer.current = setTimeout(() => {
                    setCurrentTrackInfo(null);
                }, 500);
            }
        },
        onAudioPlay: (audioInfo) => {
            markPlayTiming('onAudioPlay');
            clearTimeout(clearTrackInfoTimer.current);
            // Sync Redux autoPlay with reality: playback IS running. The
            // add-to-queue guard sets autoPlay=false for silent adds; if the
            // user then starts playback via jinke's own play button (which
            // never touches Redux), a later queue APPEND re-inits jinke's
            // list and — with autoPlay=false — paused the playing track
            // mid-song. (Desktop only: native has its own phantom-resume
            // guard below and must not flip state on iOS auto-resumes.)
            if (!isNative) dispatch(setAutoPlay(true));
            // On web, show CTA when track starts playing (sample only; upgrade requires desktop)
            notifyWebOnly('track');
            // Capacitor hybrid: drive AVPlayer in parallel. New URL → play();
            // same URL → resume() (preserves AVPlayer position so the
            // lock-screen widget keeps showing the correct elapsed time).
            //
            // The HTML5 mute is RE-ASSERTED here on every track play (not
            // only at component mount) because plugin registration via
            // MixGoatBridgeViewController.capacitorDidLoad fires AFTER the
            // mount-time useEffect runs. Without re-muting per-play, the
            // very first track would play unmuted HTML5 + AVPlayer in
            // parallel (double audio + iOS lock-screen confusion).
            if (isNative) {
                const engine = getNativePlaybackEngine();
                if (engine.isAvailable()) {
                    // Phantom-resume guard: if HTML5 audio's 'play' event
                    // fired WITHOUT a recent user touch AND the user had
                    // explicitly paused, this is iOS auto-resuming on
                    // WebView visibility change. Re-pause both sides.
                    if (userPausedRef.current && !isUserInitiatedAction()) {
                        if (audioInstanceRef.current) audioInstanceRef.current.pause();
                        engine.pause();
                        dispatch(setAutoPlay(false));
                        dispatch(setIsPlaying(false));
                        return;
                    }
                    // Genuine user-initiated play: clear the paused flag
                    // so subsequent auto-events aren't misinterpreted.
                    userPausedRef.current = false;
                    // Remote-command (lock screen / notification / Bluetooth)
                    // wiring used to live here, because at mount the plugin
                    // was not yet registered. It now lives in the mount-time
                    // playerState effect, which only runs once the engine is
                    // available and does not depend on jinke firing anything.
                    // See "Lock-screen / notification / Bluetooth transport"
                    // there — a transport control must not be hostage to a
                    // muted element's play event.
                    if (audioInstanceRef.current) {
                        audioInstanceRef.current.volume = 0;
                        audioInstanceRef.current.muted = true;
                    }
                    // ── New native contract: native owns the queue ──
                    // If jinke moved to a different URL than what we last
                    // told native, that's a track change. Translate the URL
                    // into an index in our Redux audioList and call
                    // engine.playIndex(idx). Native plugin already has the
                    // full queue from the audioList useEffect below — this
                    // just tells it which slot to play.
                    //
                    // onAudioPlay NEVER moves the plugin.
                    //
                    // jinke's element is silent on native and its src is
                    // whatever was tapped however long ago — the plugin has
                    // usually advanced well past it. Every attempt to act on
                    // this event ended the same way: something makes
                    // nativeIsPlayingRef false (a pause, a lock), jinke then
                    // fires a play of its own accord carrying that stale src,
                    // and playback teleports to it. Once the whole surface
                    // became the queue, that stale src was typically the row
                    // the user first started from — the top of the feed.
                    //
                    // It is intermittent by nature: it needs a stray event to
                    // land in a particular window, which a long freestyle
                    // session produces and a clean tap-then-pause does not.
                    //
                    // Nothing needs this call. Row taps are started by the
                    // queue effect, resume by the play/pause hijack and by
                    // DJPalAudioPreview's active-row branch, and lock-screen
                    // commands are actuated by the plugin itself. The rest of
                    // this handler (analytics, track info, remote-command
                    // wiring, re-muting) still runs.
                }
            }
            // Playback analytics: same musicSrc + unfinished session = a
            // resume (or the lazy-upgrade src swap re-firing play) — no
            // event. Anything else starts a new session; an unfinished
            // previous session was abandoned mid-track → track_skip.
            // try/catch: session bookkeeping must never break playback.
            try {
                const src = audioInfo.musicSrc || '';
                const sess = playSessionRef.current;
                if (!sess || sess.src !== src || sess.completed) {
                    if (sess && !sess.completed) {
                        if (sess.resumedAt) { sess.listenMs += Date.now() - sess.resumedAt; }
                        const listened = Math.round(sess.listenMs / 1000);
                        if (listened > 0) {
                            // Was the abandoned track still in the queue when the
                            // new one started? Yes → user advanced within the list
                            // (next/queue jump). No → the list was reset by a row
                            // click elsewhere. Feeds the prefetch engagement-gate
                            // tuning: 'next' skips would have consumed a prefetch,
                            // 'row_click' skips would have wasted one.
                            const prevInList = (audioListRef.current || []).some(t => t.musicSrc === sess.src);
                            trackEvent('track_skip', {
                                listen_seconds: listened,
                                source: sess.source,
                                end_reason: prevInList ? 'next' : 'row_click',
                            });
                        }
                    }
                    const rt = audioListRef.current?.find(t => t.musicSrc === src);
                    const source = (rt?.filePath || src.includes('localhost:3069/audio')) ? 'local'
                        : (rt?.youtubeId && rt.youtubeId !== '-1') ? 'youtube'
                        : rt?.soundcloudUrl ? 'soundcloud'
                        : 'sample';
                    playSessionRef.current = { src, source, isMix: !!rt?.isMix, mixId: rt?.mixId, listenMs: 0, resumedAt: Date.now(), pausedReportedMs: 0, completed: false };
                    trackEvent('track_play', {
                        name: audioInfo.name || '',
                        singer: audioInfo.singer || '',
                        track_id: rt?.id || undefined,
                        mix_id: rt?.mixId || undefined,
                        is_mix: rt?.isMix || undefined,
                        source,
                        // origin (feed | trending) is finer than the list type ('tracks'
                        // for every card list) — it is what the discovery funnel keys on.
                        from: listContextRef.current?.origin || listContextRef.current?.type || undefined,
                    });
                } else if (!sess.resumedAt) {
                    sess.resumedAt = Date.now();
                }
            } catch { /* analytics must never break playback */ }
            dispatch(setAutoPlay(true));
            // Mirror current track + playing state into Redux so the
            // mobile NowPlayingPage can render without duplicating the
            // library's state tracking.
            dispatch(setCurrentTrack({
                name: audioInfo.name || '',
                singer: audioInfo.singer || '',
                cover: resolveCover(audioInfo.cover),
                musicSrc: audioInfo.musicSrc || '',
            }));
            dispatch(setIsPlaying(true));
            const reduxTrack = audioListRef.current?.find(t => t.musicSrc === audioInfo.musicSrc);
            const sampleSrc = audioInfo.musicSrc;
            const prevUpgrade = upgradedSrcRef.current.get(sampleSrc);
            const effectiveOriginalUrl = prevUpgrade?.originalUrl || reduxTrack?.originalUrl || null;

            // If this track was previously upgraded (e.g. user paused and resumed),
            // re-swap the audio element's src to the extracted stream instead of
            // letting jinke play the raw sample URL again.
            if (prevUpgrade) {
                const audio = window.musicPlayerInstance;
                if (audio && audio.src !== prevUpgrade.streamUrl) {
                    audio.src = prevUpgrade.streamUrl;
                    audio.play().catch(() => {});
                }
            }

            // Stash current musicSrc + originalUrl + trackId so lazily-mounted
            // FeedWaveform / MixFeedWaveform components can sync up without a
            // prior event (e.g. after the user navigates away and back).
            // trackId matters when the played URL DIFFERS from what the row
            // derives from its own track data (dead DB link replaced at play
            // time): src/originalUrl matching both fail there, and the row
            // needs the id to recognize its track and adopt the live URL.
            // On native (with the plugin up) the playerState stream owns this
            // broadcast: jinke's muted element carries whatever src was tapped
            // however long ago, and re-stamping/broadcasting it here re-lit
            // the OLD row after the plugin had moved on.
            if (!isNative || !getNativePlaybackEngine().isAvailable()) {
                window.__currentPlayerMusicSrc = sampleSrc || null;
                window.__currentPlayerOriginalUrl = effectiveOriginalUrl;
                window.__currentPlayerTrackId = (reduxTrack?.trackId ?? reduxTrack?.id) != null ? String(reduxTrack.trackId ?? reduxTrack.id) : null;
                window.__currentPlayerMixId = reduxTrack?.mixId != null ? String(reduxTrack.mixId) : null;
                window.dispatchEvent(new CustomEvent('player-track-change', {
                    detail: { musicSrc: sampleSrc, originalUrl: effectiveOriginalUrl, trackId: window.__currentPlayerTrackId, mixId: window.__currentPlayerMixId, playing: true },
                }));
            }

            // No mid-play source swap. Every queue builder resolves the full
            // track before it plays (resolveTrackSrc / /lazy-audio), so an entry
            // that still starts on its sample has no full source, is blocked
            // (takedown), or is a local file. The old background upgrade here
            // swapped those to YouTube mid-play (sample first, then full).

            // During skip, don't update track info (prevents waveform flash)
            if (skipInProgressRef.current) return;
            // Only update if the actual track changed
            setCurrentTrackInfo(prev => {
                if (prev && prev.musicSrc === sampleSrc) return prev;
                return reduxTrack
                    ? { ...audioInfo, ...reduxTrack, originalUrl: effectiveOriginalUrl }
                    : { ...audioInfo, originalUrl: effectiveOriginalUrl };
            });
        },
        onAudioPlayTrackChange: (playId, audioLists, audioInfo) => {
            // Fires the MOMENT jinke switches to a new entry — before the
            // audio loads (lazy entries take seconds to extract). Sync the
            // waveform-mode header (title/artist from currentTrackInfo)
            // right away; updating it at onAudioPlay left a seconds-long
            // window where the cover showed the next track while the header
            // still named the previous one.
            //
            // NOTE: `audioInfo` here is STALE — jinke fires this callback
            // BEFORE updating its own state, so it describes the PREVIOUS
            // track. Resolve the NEW track from playId + jinke's list.
            if (skipInProgressRef.current) return;
            const next = Array.isArray(audioLists)
                ? audioLists.find(a => a && a.__PLAYER_KEY__ === playId)
                : null;
            const src = next?.musicSrc;
            if (!src) return;
            clearTimeout(clearTrackInfoTimer.current);
            const reduxTrack = audioListRef.current?.find(t => t.musicSrc === src);
            setCurrentTrackInfo(prev => {
                if (prev && prev.musicSrc === src) return prev;
                const base = { name: next.name, singer: next.singer, cover: next.cover, musicSrc: src };
                return reduxTrack ? { ...base, ...reduxTrack } : base;
            });
        },
        onAudioPause: (audioInfo) => {
            // Phantom-pause guard: iOS pauses HTML5 audio when the
            // WebView visibility flips to hidden (e.g., screen lock).
            // jinke fires onAudioPause for that, but it's NOT a user
            // pause — propagating to AVPlayer kills the music the user
            // expected to keep playing on lock. Skip the propagation
            // if no user touch was recent. Same heuristic as the
            // onAudioPlay phantom-resume guard.
            // NATIVE: never relay a pause from jinke's element. iOS pauses it
            // on EVERY backgrounding, and backgrounding is itself a gesture —
            // so the old !isUserInitiatedAction() guard let it through and
            // minimizing the app stopped the music. A genuine pause comes from
            // the play/pause hijack, which knows for certain. Redux isPlaying
            // is refreshed from the plugin at 4Hz regardless.
            if (isNative) return;
            // Genuine user pause — flag it for the onAudioPlay guard.
            userPausedRef.current = true;
            // Playback analytics: bank listen time; report the pause once
            // it covers ≥2s of NEW listening (skips pause/seek stutter).
            try {
                const sess = playSessionRef.current;
                if (sess && sess.resumedAt) {
                    sess.listenMs += Date.now() - sess.resumedAt;
                    sess.resumedAt = null;
                    if (!sess.completed && sess.listenMs - sess.pausedReportedMs >= 2000) {
                        sess.pausedReportedMs = sess.listenMs;
                        trackEvent('track_pause', { listen_seconds: Math.round(sess.listenMs / 1000) });
                    }
                }
            } catch { /* analytics must never break playback */ }
            dispatch(setAutoPlay(false));
            dispatch(setIsPlaying(false));
            // Broadcast pause so any DJPalAudioPreview / row UI showing this
            // track flips its play icon. Without this the bottom-player pause
            // looks correct, but row icons stay stuck on "playing".
            window.dispatchEvent(new CustomEvent('player-track-change', {
                detail: {
                    musicSrc: window.__currentPlayerMusicSrc || null,
                    originalUrl: window.__currentPlayerOriginalUrl || null,
                    playing: false,
                },
            }));
        },
        onAudioEnded: (currentPlayId, audioLists, audioInfo) => {
            // DO NOT clear nativeAudioUrlRef on native. The native plugin
            // owns the queue and may have already auto-advanced. Clearing
            // the ref means jinke's subsequent muted onAudioPlay (which
            // refires after a sample reaches its 30s end) gets treated as
            // a new track → onAudioPlay calls engine.playIndex(0), forcing
            // native BACK to the first track. Keep the ref pinned so jinke
            // re-events for the same URL get the resume() branch.
            // Playback analytics: the track ran to its end (fires on both
            // desktop and native paths — must precede the isNative return).
            try {
                const sess = playSessionRef.current;
                if (sess && !sess.completed && (!audioInfo?.musicSrc || sess.src === audioInfo.musicSrc)) {
                    if (sess.resumedAt) { sess.listenMs += Date.now() - sess.resumedAt; sess.resumedAt = null; }
                    sess.completed = true;
                    const dur = Number(window.musicPlayerInstance?.duration);
                    const completeProps = {
                        mix_id: sess.mixId || undefined,
                        listen_seconds: Math.round(sess.listenMs / 1000),
                        duration_seconds: Number.isFinite(dur) && dur > 0 ? Math.round(dur) : undefined,
                        source: sess.source,
                    };
                    if (sess.isMix) trackEvent('mix_play_complete', completeProps);
                    else trackEvent('track_play_complete', completeProps);
                }
            } catch { /* analytics must never break playback */ }

            // Mark current track as played for radio dedup
            if (autoRadio && audioInfo?.name) {
                dispatch(addPlayedTrackKey(`${audioInfo.singer || ''}|||${audioInfo.name}`));
            }

            // Native owns queue extension via the plugin's setRefillContext
            // (both AVPlayer on iOS and ExoPlayer on Android). If JS also
            // runs advanceListContext / autoRadio here, we race with
            // native and audio glitches as setQueue replaces native's
            // queue mid-play. Skip the JS-side refill on native; the
            // plugin handles it.
            if (isNative) return;

            const ctx = listContextRef.current;
            const hasListContext = ctx?.type && ctx.items?.length;
            const hasMoreInList = hasListContext && ctx.currentIndex < ctx.items.length - 1;
            const justExhaustedList = hasListContext && !hasMoreInList;

            // jinke never pops played tracks from audioLists — it just
            // advances an internal index. So after a few auto-radio
            // appends, audioLists.length is permanently > 3 and the
            // legacy "queue running low" check stops firing. Compute
            // "tracks after the one that just ended" instead so we can
            // detect the actual end-of-queue.
            const endedSrc = audioInfo?.musicSrc;
            const endedIdx = Array.isArray(audioLists)
                ? audioLists.findIndex((t) => t?.musicSrc === endedSrc)
                : -1;
            const tracksAfter = endedIdx >= 0
                ? Math.max(0, audioLists.length - 1 - endedIdx)
                : 0;
            const queueExhausted = tracksAfter === 0;

            console.warn('[MusicPlayer] onAudioEnded', {
                track: audioInfo?.name,
                hasMoreInList,
                ctxType: ctx?.type,
                currentIndex: ctx?.currentIndex,
                itemsLength: ctx?.items?.length,
                advancing: listContextAdvancingRef.current,
                endedIdx,
                queueLength: audioLists?.length,
                tracksAfter,
            });

            // Manually queued tracks (Add to Next Up) play FIRST: when tracks
            // remain after the one that just ended, return and let jinke
            // advance through its own queue. The ambient list context (feed /
            // playlist autoplay) resumes only once the queue is exhausted —
            // mirrors the next-button's priority. Unconditional context-first
            // here made queued tracks unreachable (context never runs out).
            if (hasMoreInList) {
                if (!queueExhausted) return; // jinke advances to the next queued track
                // Re-anchor the context to the just-ended track's position in
                // the ambient list: after a manually-built queue plays out,
                // resume from where the LAST QUEUED track sits in the list —
                // not from the stale index of whatever was playing when the
                // queue was built (which picked a seemingly random track).
                const endedKey = `${(audioInfo?.singer || '').toLowerCase()}|||${(audioInfo?.name || '').toLowerCase()}`;
                const itemKey = (it) => `${(it?.artist || it?.djName || it?.dj_name || '').toLowerCase()}|||${(it?.title || '').toLowerCase()}`;
                const anchorIdx = ctx.items.findIndex(it => itemKey(it) === endedKey);
                if (anchorIdx >= 0 && anchorIdx !== ctx.currentIndex) {
                    dispatch(setListContextIndex(anchorIdx));
                    listContextRef.current = { ...ctx, currentIndex: anchorIdx };
                }
                advanceListContext();
                return;
            }

            // If likes playback and queue is running low, fetch next batch
            if (likesPlayback && audioLists.length <= 3) {
                fetchNextLikesBatch();
            }
            // Auto-radio: fire when the list just exhausted, or when the
            // just-ended track was the last item in jinke's queue (so
            // there's nothing for the player to advance to).
            else if (autoRadio && !likesPlayback && (justExhaustedList || queueExhausted)) {
                const trackInfo = currentTrackInfoRef.current;
                if (trackInfo?.name) {
                    fetchRadioRecommendations(trackInfo.name, trackInfo.singer, radioSeedsFromContext());
                }
            }
        },
        onAudioError: async (errMsg, currentPlayId, audioLists, audioInfo) => {
            console.warn('[MusicPlayer] onAudioError fired:', { errMsg, musicSrc: audioInfo?.musicSrc, name: audioInfo?.name });

            // NATIVE: jinke's muted element cannot fail playback — the plugin
            // carries the sound and reports real failures on the engine's
            // 'error' channel (handled in the playerState effect). What lands
            // HERE on native is jinke choking on its own mirror state — e.g.
            // currentPlayId holds the PLUGIN queue index (the whole surface)
            // while jinke's list has 1-2 items, so after a skip jinke tries
            // to play an out-of-range entry and errors. The advance-on-error
            // recovery below then yanked the REAL player off the track it
            // was playing ~100ms after every skip to a queued track (found
            // via the on-device E2E, 2026-08-21). Same rule as onAudioPause:
            // never act on the muted mirror.
            if (isNative && getNativePlaybackEngine().isAvailable()) return;

            // Suppress errors for HLS tracks — hls.js will handle playback
            const isHlsTrack = audioListRef.current?.some(t => t.musicSrc === audioInfo?.musicSrc && t.isHls);
            if (isHlsTrack) return;

            const errString = typeof errMsg === 'string' ? errMsg : String(errMsg || '');
            const isPauseOrStop = errString.toLowerCase().includes('pause') || errString.toLowerCase().includes('stop');
            if (isPauseOrStop) return;

            // Look up the failed track in our Redux audioList to get original URL + sample
            const failedTrack = audioListRef.current?.find(t => t.musicSrc === audioInfo?.musicSrc);
            const trackName = audioInfo?.name || failedTrack?.name || 'Track';

            // ── Recover from an expired/403 stream URL by re-resolving a fresh one ──
            // YouTube googlevideo URLs are signed with a ~6h lifetime. If the track
            // sat in the player (e.g. paused overnight) the URL expires and the CDN
            // returns 403 → FFmpegDemuxer data source error. Rather than failing
            // silently, re-extract a fresh stream URL and resume from where we
            // stopped (restart only if the position can't be restored).
            const sampleSrc = audioInfo?.musicSrc;
            const prevUpgrade = sampleSrc ? upgradedSrcRef.current.get(sampleSrc) : null;
            const ytId = failedTrack?.youtubeId && failedTrack.youtubeId !== '-1' ? failedTrack.youtubeId : null;
            const fullPlayUrl = prevUpgrade?.originalUrl
                || failedTrack?.originalUrl
                || (ytId ? `https://www.youtube.com/watch?v=${ytId}` : (failedTrack?.soundcloudUrl || null));
            const audioForRetry = window.musicPlayerInstance;
            if (sampleSrc && recoveryInFlightRef.current.has(sampleSrc)) {
                console.warn('[MusicPlayer] Recovery already in flight for this track — ignoring duplicate error');
                return;
            }
            const alreadyRetried = sampleSrc ? streamRetryRef.current.has(sampleSrc) : true;
            // "Is the failed track still the one in the player?" — checked
            // before every recovery swap below. `__currentPlayerMusicSrc` is
            // only stamped by onAudioPlay, which never fires for a track that
            // dies before `canplay` (a queued /lazy-audio entry 403'ing on its
            // very first load). Keying the guard on it alone made both
            // rescues (fresh stream, downloaded file) unreachable for exactly
            // the cold-load failures they exist for. So ALSO accept the
            // element still sitting on the src we're recovering (`ownSrc`
            // tracks our own swaps; a user skip replaces the src with the
            // next track's and fails both tests).
            let ownSrc = audioForRetry?.src || null;
            const isStillCurrent = () => window.musicPlayerInstance === audioForRetry
                && (window.__currentPlayerMusicSrc === sampleSrc || (!!ownSrc && audioForRetry.src === ownSrc));

            if (fullPlayUrl && audioForRetry && !alreadyRetried && window.electron?.djpal?.extractStreamUrl) {
                streamRetryRef.current.add(sampleSrc);
                recoveryInFlightRef.current.add(sampleSrc);
                // Resume position: prefer the element's current time, fall back to
                // the last position we observed via 'timeupdate' (the element may
                // reset to 0 on a load error). A track that never started (cold
                // load failure) restarts at 0 — lastPositionRef still holds the
                // PREVIOUS track's position.
                const hadPlayed = window.__currentPlayerMusicSrc === sampleSrc;
                const resumeAt = (audioForRetry.currentTime > 0 ? audioForRetry.currentTime : 0)
                    || (hadPlayed ? lastPositionRef.current : 0) || 0;
                try {
                    // Drop the stale cache entry so extraction re-runs yt-dlp instead of
                    // handing back the same expired URL (success entries cache for 30m).
                    if (window.electron?.djpal?.invalidateStreamCache) {
                        try { window.electron.djpal.invalidateStreamCache(fullPlayUrl); } catch (_) {}
                    }
                    // Recovery is silent: re-extracting a fresh URL after an
                    // expired/403 stream is routine plumbing, not something the
                    // user should be made aware of. Log for debugging only.
                    console.warn('[MusicPlayer] Stream error — re-resolving fresh URL for', trackName, '| resumeAt', resumeAt);
                    try {
                        const result = await window.electron.djpal.extractStreamUrl(fullPlayUrl);
                        if (result?.streamUrl && !result.streamUrl.includes('.mpd')) {
                            const streamUrl = result.streamUrl.startsWith('http://localhost:3069/')
                                ? result.streamUrl
                                : `http://localhost:3069/stream-proxy?url=${encodeURIComponent(result.streamUrl)}`;
                            // Only resume if the user hasn't navigated to a different track.
                            if (isStillCurrent()) {
                                upgradedSrcRef.current.set(sampleSrc, { streamUrl, originalUrl: fullPlayUrl });
                                const restorePosition = () => {
                                    try {
                                        if (resumeAt > 0 && (!audioForRetry.duration || resumeAt < audioForRetry.duration)) {
                                            audioForRetry.currentTime = resumeAt;
                                        }
                                    } catch (_) {}
                                    audioForRetry.removeEventListener('loadedmetadata', restorePosition);
                                };
                                audioForRetry.addEventListener('loadedmetadata', restorePosition);
                                audioForRetry.src = streamUrl;
                                ownSrc = audioForRetry.src;
                                await audioForRetry.play();
                                // Recovered — allow a future expiry of this same track to
                                // recover again (clear the one-shot guard shortly after).
                                setTimeout(() => streamRetryRef.current.delete(sampleSrc), 5000);
                                return;
                            }
                            console.warn('[MusicPlayer] Re-resolve succeeded but the player moved on — dropping it');
                        } else {
                            console.warn('[MusicPlayer] Re-resolve returned no usable stream URL, falling through');
                        }
                    } catch (err) {
                        console.warn('[MusicPlayer] Re-resolve attempt failed, falling through:', err);
                    }
                    // Last resort before giving up on the track: the DOWNLOADED
                    // file. A signed googlevideo URL is IP- and time-locked and can
                    // 403 twice in a row, but the full audio is usually already on
                    // disk — the waveform downloads it for every track that plays —
                    // and it's served from our own audio server, which can't 403.
                    // Skipping a track whose audio is sitting in the cache is the
                    // "Skipping X — audio unavailable" lie the user hit.
                    try {
                        if (window.electron?.djpal?.getTrackAudio) {
                            // Bounded: a cached file comes back instantly (the case
                            // that matters), a cold download can take minutes and
                            // must not freeze the player on a dead track. Losing
                            // the race just means we skip — the download keeps
                            // running and warms the cache for the next attempt.
                            const dl = await Promise.race([
                                window.electron.djpal.getTrackAudio(fullPlayUrl, { priority: true }),
                                new Promise((r) => setTimeout(() => r(null), 15000)),
                            ]);
                            if (dl?.filePath && isStillCurrent()) {
                                const fileSrc = `http://localhost:3069/audio?path=${encodeURIComponent(dl.filePath)}`;
                                console.warn('[MusicPlayer] Stream unusable — playing the downloaded file instead for', trackName);
                                upgradedSrcRef.current.set(sampleSrc, { streamUrl: fileSrc, originalUrl: fullPlayUrl });
                                const restoreFromFile = () => {
                                    try {
                                        if (resumeAt > 0 && (!audioForRetry.duration || resumeAt < audioForRetry.duration)) {
                                            audioForRetry.currentTime = resumeAt;
                                        }
                                    } catch (_) {}
                                    audioForRetry.removeEventListener('loadedmetadata', restoreFromFile);
                                };
                                audioForRetry.addEventListener('loadedmetadata', restoreFromFile);
                                audioForRetry.src = fileSrc;
                                ownSrc = audioForRetry.src;
                                await audioForRetry.play();
                                setTimeout(() => streamRetryRef.current.delete(sampleSrc), 5000);
                                return;
                            }
                            console.warn('[MusicPlayer] Downloaded-file fallback unusable', {
                                hasFile: !!dl?.filePath, stillCurrent: isStillCurrent(), error: dl?.error,
                            });
                        }
                    } catch (err) {
                        console.warn('[MusicPlayer] Downloaded-file fallback failed, falling through:', err);
                    }
                } finally {
                    recoveryInFlightRef.current.delete(sampleSrc);
                }
                // Re-resolve failed: fall through to the existing skip/sample fallbacks.
            }

            // Circuit breaker: three different tracks failed without a second
            // of audio in between — stop instead of walking the whole queue.
            if (audioInfo?.musicSrc) failedSinceAudioRef.current.add(audioInfo.musicSrc);
            if (failedSinceAudioRef.current.size >= MAX_FAILED_IN_A_ROW) {
                const n = failedSinceAudioRef.current.size;
                failedSinceAudioRef.current = new Set();
                console.warn('[MusicPlayer] ' + n + ' tracks failed in a row — stopping playback');
                resetStuckPlayer(n + ' tracks failed in a row');
                dispatch(clearListContext());
                toast.warning(`Playback stopped — ${n} tracks in a row couldn't be played.`, 7000);
                return;
            }

            // Purge the cached bad stream URL so the next extraction is fresh
            // (yt-dlp may pick a different player_client and produce a working URL).
            if (failedTrack?.originalUrl && window.electron?.djpal?.invalidateStreamCache) {
                try { window.electron.djpal.invalidateStreamCache(failedTrack.originalUrl); } catch (_) {}
            }

            // Reset row-level loading/playing UI so spinners don't get stuck
            window.dispatchEvent(new CustomEvent('djpal-track-stop-all'));
            window.dispatchEvent(new CustomEvent('djpal-mix-stop'));

            // Skip to next in list context on error (expired YouTube URLs, 403, etc.)
            const ctx = listContextRef.current;
            // A local audio-server 404 means the file isn't on this machine
            // (playlist synced from another computer, unmounted drive).
            const isLocalMissing = typeof audioInfo?.musicSrc === 'string'
                && audioInfo.musicSrc.includes('localhost:3069/audio?path=');

            // Manually queued tracks first (same priority as onAudioEnded):
            // when more queue entries remain after the failed one, skip to the
            // next QUEUE entry — advancing the ambient list context here
            // yanked playback back into the feed mid-queue.
            const queueNow = audioListRef.current || [];
            const failedQueueIdx = queueNow.findIndex(t => t?.musicSrc === audioInfo?.musicSrc);
            if (failedQueueIdx >= 0 && failedQueueIdx < queueNow.length - 1) {
                if (isLocalMissing) reportMissingFile();
                else warnAudioUnavailable(trackName);
                if (userPausedRef.current) {
                    console.warn('[MusicPlayer] Audio error while paused — not auto-advancing:', errMsg);
                    return;
                }
                console.warn('[MusicPlayer] Audio error mid-queue, skipping to next queued track:', errMsg);
                const nextItem = findJinkeQueueItem(queueNow, failedQueueIdx + 1);
                if (nextItem) {
                    nextItem.click();
                    return;
                }
                // Panel not rendered — fall through to the list-context skip.
            }
            const alreadyToasted = failedQueueIdx >= 0 && failedQueueIdx < queueNow.length - 1;

            if (ctx?.type && ctx.items?.length && ctx.currentIndex < ctx.items.length - 1) {
                // Say WHY before moving on — a silent jump reads as the player
                // randomly changing tracks. Local missing files use the
                // aggregated "N tracks aren't on this device" toast.
                if (!alreadyToasted) {
                    if (isLocalMissing) reportMissingFile();
                    else warnAudioUnavailable(trackName);
                }
                // A paused player must stay paused: auto-advancing would yank
                // the user to a different track and start playing it.
                if (userPausedRef.current) {
                    console.warn('[MusicPlayer] Audio error while paused — not auto-advancing:', errMsg);
                    return;
                }
                console.warn('[MusicPlayer] Audio error in list context, skipping to next:', errMsg);
                // Re-anchor to the FAILED track's position in the list (same
                // as onAudioEnded): a queue-built-from-feed failure must
                // resume after the failed track, not from a stale index.
                const failedKey = `${(audioInfo?.singer || '').toLowerCase()}|||${(audioInfo?.name || '').toLowerCase()}`;
                const ctxItemKey = (it) => `${(it?.artist || it?.djName || it?.dj_name || '').toLowerCase()}|||${(it?.title || '').toLowerCase()}`;
                const failAnchor = ctx.items.findIndex(it => ctxItemKey(it) === failedKey);
                if (failAnchor >= 0 && failAnchor !== ctx.currentIndex) {
                    dispatch(setListContextIndex(failAnchor));
                    listContextRef.current = { ...ctx, currentIndex: failAnchor };
                }
                advanceListContext({ fromError: true });
                return;
            }

            // Single-play fallback: swap to sample preview if available
            const samplePreviewUrl = failedTrack?.samplePreviewUrl;
            if (samplePreviewUrl && samplePreviewUrl !== failedTrack.musicSrc) {
                console.warn('[MusicPlayer] Stream failed, falling back to sample preview:', samplePreviewUrl);
                const audio = window.musicPlayerInstance;
                if (audio) {
                    upgradedSrcRef.current.delete(failedTrack.musicSrc);
                    audio.src = samplePreviewUrl;
                    // The player's WaveformOverlay is keyed on the ENTRY's
                    // musicSrc/originalUrl and was fetching full-track peaks
                    // for a source that just died — it would sit on a blank
                    // seek strip while the ~30s sample plays. Tell it the
                    // audible clip changed (same contract as lazy-audio-
                    // fallback): decode the sample instead. The Map replays
                    // the answer for an overlay that (re)mounts after this
                    // fires (waveform toggle, mode switch).
                    try {
                        (window.__sampleFallbacks = window.__sampleFallbacks || new Map()).set(failedTrack.musicSrc, samplePreviewUrl);
                        window.dispatchEvent(new CustomEvent('player-sample-fallback', {
                            detail: { musicSrc: failedTrack.musicSrc, sampleUrl: samplePreviewUrl },
                        }));
                    } catch (_) { /* best-effort */ }
                    audio.play().catch((err) => {
                        console.error('[MusicPlayer] Sample fallback also failed:', err);
                        toast.error(`Unable to play ${trackName}. Both stream and preview failed.`);
                        // Nothing left to try — don't leave the loader spinning
                        // on the dead preview.
                        resetStuckPlayer('sample fallback also failed');
                    });
                    showSampleFallbackToast(trackName);
                    return;
                }
            }

            // A local library file that 404s isn't on this machine — almost
            // always a playlist synced from another computer (metadata only, no
            // audio). Don't spam a per-track error; aggregate into one clear,
            // debounced toast that explains why.
            if (isLocalMissing && !samplePreviewUrl) {
                console.warn('[MusicPlayer] Local file unavailable (likely synced from another machine):', audioInfo?.musicSrc);
                reportMissingFile();
                // If there's nothing AFTER this track for jinke's error-advance
                // to move on to (single-track queue, or the cascade just hit
                // the last track), the dead source would spin its loader
                // forever. Reset the player instead.
                const listLen = audioLists?.length || 0;
                const erroredIdx = Array.isArray(audioLists)
                    ? audioLists.findIndex(a => a?.musicSrc === audioInfo?.musicSrc)
                    : -1;
                if (listLen <= 1 || erroredIdx === listLen - 1) {
                    resetStuckPlayer('no next track after failed local file');
                }
                return;
            }

            console.error('[MusicPlayer] Audio error (no fallback available):', errMsg, audioInfo);
            errorAudioUnavailable(trackName);
            // Infinity mode: keep playback going by seeding auto-radio from the
            // failed track instead of leaving the player silent.
            if (autoRadioRef.current) {
                fetchRadioRecommendations(trackName, audioInfo?.singer);
                return;
            }
            // Terminal: every recovery above is exhausted (no re-resolve, no
            // downloaded file, no next queue entry, no list context, no sample,
            // no radio). The dead source is still loaded and jinke is still
            // flagged `loading` — reset, or the spinner never stops and the
            // track can't even be retried.
            resetStuckPlayer('audio error with no fallback');
        },
    });

    const toggleContainer = document.getElementById('waveform-toggle-container');
    const waveformContainer = document.getElementById('waveform-replacement-container');
    const saveButtonContainer = document.getElementById('save-playlist-button-container');

    // DESIGN V2: the bar is always there (Spotify-style); jinke only paints a
    // floating disc while the queue is empty, so draw a quiet empty bar.
    const designV2 = useDesignV2();
    const v2EmptyBar = designV2 && audioList.length === 0 ? (
        <div className="v2-player-empty" aria-hidden="true">
            <span className="v2-player-empty-note">Nothing playing</span>
            <span className="v2-player-empty-transport">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h2v12H6zM9.5 12l8.5 6V6z" /></svg>
                <i><svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg></i>
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 6h2v12h-2zM6 18l8.5-6L6 6z" /></svg>
            </span>
        </div>
    ) : null;
    return (
        <div className={`music-player-container ${showWaveform ? 'waveform-active' : ''} ${mode === 'mini' ? 'player-mini-mode' : ''} ${autoRadio ? 'auto-radio-active' : ''}`}>
            {v2EmptyBar}
            <AudioErrorBoundary>
                <ReactJkMusicPlayer
                    {...getOptions()}
                    getAudioInstance={(instance) => {
                        audioInstanceRef.current = instance;
                    }}
                />
            </AudioErrorBoundary>

            {/* Render toggle button + like button to the left of player-content */}
            {toggleContainer && mode === 'full' && ReactDOM.createPortal(
                <>
                    {!isMobile && <button
                        className={`waveform-toggle-btn ${showWaveform ? 'active' : ''}`}
                        onClick={() => setShowWaveform(!showWaveform)}
                        title={showWaveform ? 'Hide Waveform' : 'Show Waveform'}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M12 4v16M8 8v8M16 8v8M4 10v4M20 10v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>}
                    {currentTrackInfo?.singer && currentTrackInfo?.name && (
                        <div className="player-like-wrap">
                            {currentTrackInfo.isMix ? (
                                <MixLikeButton mix={{
                                    title: currentTrackInfo.mixTitle || currentTrackInfo.name,
                                    djName: currentTrackInfo.singer,
                                    artwork: currentTrackInfo.cover,
                                    link: currentTrackInfo.originalUrl,
                                    id: currentTrackInfo.mixId,
                                }} />
                            ) : (() => {
                                const yt = extractYoutubeId(currentTrackInfo.originalUrl);
                                const isSc = currentTrackInfo.originalUrl?.includes('soundcloud.com');
                                // A genuinely-local library track has no web provenance
                                // (no YouTube/SoundCloud origin, no catalog id, no Apple
                                // sample) — only then is the player's musicSrc a real
                                // local-file URL worth remembering so the likes list can
                                // play it from disk. For Mixpedia tracks the musicSrc is a
                                // throwaway yt-dlp temp file, so we must NOT treat it as a
                                // local library path.
                                const isLocalPlayback = !currentTrackInfo.originalUrl
                                    && !currentTrackInfo.trackId
                                    && !currentTrackInfo.samplePreviewUrl;
                                return <LikeButton track={{
                                    artist: currentTrackInfo.singer,
                                    title: currentTrackInfo.name,
                                    artwork: currentTrackInfo.cover,
                                    previewUrl: currentTrackInfo.samplePreviewUrl || null,
                                    youtubeId: yt,
                                    soundcloudUrl: isSc ? currentTrackInfo.originalUrl : null,
                                    id: currentTrackInfo.trackId || null,
                                    musicSrc: isLocalPlayback ? (currentTrackInfo.musicSrc || null) : null,
                                }} />;
                            })()}
                        </div>
                    )}
                    {!isMobile && currentTrackInfo?.singer && currentTrackInfo?.name && (
                        <button
                            className="player-search-btn"
                            title="Search in Mixpedia"
                            onClick={handleSearchInMixpedia}
                        >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                            </svg>
                        </button>
                    )}
                </>,
                toggleContainer
            )}

            {/* Render waveform in place of progress-bar-content */}
            {waveformContainer && mode === 'full' && showWaveform && audioElement && currentTrackInfo && ReactDOM.createPortal(
                <div className="waveform-replacement">
                    <div className="waveform-track-overlay">
                        <span
                            className="waveform-track-name clickable"
                            onClick={handleSearchInMixpedia}
                            title="Open in Mixpedia"
                        >{currentTrackInfo.name}</span>
                        {currentTrackInfo.singer && (
                            <MultiArtistText
                                artists={currentTrackInfo.artists}
                                fallbackText={currentTrackInfo.singer}
                                onSelect={handleOpenArtist}
                                className="waveform-track-artist"
                                segmentClassName="clickable-artist"
                            />
                        )}
                        {currentBpm != null && <span className="waveform-track-bpm" title="Tempo">{currentBpm} BPM</span>}
                        <span className="waveform-track-time">{formatTime(currentTime)} / {formatTime(duration)}</span>
                    </div>
                    <WaveformOverlay key={currentTrackInfo?.musicSrc} audioElement={audioElement} sourceUrl={currentTrackInfo?.musicSrc} originalUrl={currentTrackInfo?.originalUrl} />
                </div>,
                waveformContainer
            )}

            {/* Render save button in audio-lists-panel-header-actions */}
            {saveButtonContainer && audioList.length > 0 && ReactDOM.createPortal(
                <span
                    className="audio-lists-panel-header-save-btn"
                    onClick={handleSaveToPlaylist}
                    title={selectedPlaylistId && selectedPlaylistName ? `Update "${selectedPlaylistName}"` : 'Save current queue as playlist'}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="17 21 17 13 7 13 7 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="7 3 7 8 15 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </span>,
                saveButtonContainer
            )}
            {/* Custom playlist panel */}
            <CustomPlaylistPanel
                visible={customPanelOpen}
                onClose={() => setCustomPanelOpen(false)}
                currentTrackInfo={currentTrackInfo}
                autoPlay={autoPlay}
            />
        </div>
    );
};

export default MusicPlayer;
